import React from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Building2, FileText, Package, Calculator, CheckCircle2, XCircle, Save, FileDown, Key, ClipboardList, Download, Printer, PackageX, RotateCcw, ArrowLeftRight, AlertTriangle, Lock, Unlock, Receipt, Activity, BarChart3, Truck, TrendingUp, Bell, Clock, Mail, CreditCard, Send, RefreshCw, Rocket, BookOpen, ShieldCheck } from "lucide-react";
import { adminApi, API } from "../../lib/api";
import QuoteRequestsPanel from "./QuoteRequestsPanel";

const TABS = [
  { k: "control", label: "Control Tower", icon: Activity },
  { k: "alerts", label: "Alert Center", icon: Bell },
  { k: "quotereqs", label: "Tilbud / Forespørgsler", icon: Mail },
  { k: "customers", label: "Kunder", icon: Building2 },
  { k: "agreements", label: "Aftaler", icon: FileText },
  { k: "parcels", label: "Pakker", icon: Package },
  { k: "manifests", label: "Pickup-manifests", icon: ClipboardList },
  { k: "failed", label: "Fejl & returer", icon: PackageX },
  { k: "shifts", label: "Skifter & pauser", icon: Clock },
  { k: "emails", label: "Afslutningsmails", icon: Mail },
  { k: "invoice", label: "Faktura-grundlag", icon: Receipt },
  { k: "billing", label: "B2B Billing", icon: CreditCard },
  { k: "launch", label: "Launch Pack (2R-5)", icon: Rocket },
  { k: "settlement", label: "Vognmand-afregning", icon: Truck },
  { k: "finance", label: "Finance Overview", icon: TrendingUp },
  { k: "reports", label: "Rapporter", icon: BarChart3 },
  { k: "quote", label: "Pris-beregner", icon: Calculator },
  { k: "api", label: "API & integrationer", icon: Key },
];

export default function B2BParcelsPanel() {
  const [tab, setTab] = React.useState("customers");
  const [customers, setCustomers] = React.useState([]);

  const refreshCustomers = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/b2b/customers");
      setCustomers(data.items || []);
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { refreshCustomers(); }, [refreshCustomers]);

  return (
    <div data-testid="b2b-parcels-panel">
      <div className="flex gap-1 border-b border-brand mb-6 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`b2b-subtab-${t.k}`}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap flex items-center gap-2 ${
                tab === t.k ? "border-brand-red text-brand-red" : "border-transparent text-brand-text2 hover:text-brand-dark"
              }`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "control" && <ControlTowerView/>}
      {tab === "alerts" && <AlertCenterView/>}
      {tab === "quotereqs" && <QuoteRequestsPanel token={localStorage.getItem("ema_admin_token")} />}
      {tab === "customers" && <CustomersView customers={customers} refresh={refreshCustomers} />}
      {tab === "agreements" && <AgreementsView customers={customers} />}
      {tab === "parcels" && <ParcelsView customers={customers} />}
      {tab === "manifests" && <ManifestsView customers={customers} />}
      {tab === "failed" && <FailedReturnsView customers={customers} />}
      {tab === "shifts" && <ShiftsView/>}
      {tab === "emails" && <RouteEmailsView/>}
      {tab === "invoice" && <InvoiceBasisView customers={customers}/>}
      {tab === "billing" && <BillingDashboardView customers={customers} refresh={refreshCustomers}/>}
      {tab === "launch" && <LaunchPackView customers={customers} refresh={refreshCustomers}/>}
      {tab === "settlement" && <SettlementView/>}
      {tab === "finance" && <FinanceOverviewView/>}
      {tab === "reports" && <ReportsView customers={customers}/>}
      {tab === "quote" && <QuoteView customers={customers} />}
      {tab === "api" && <ApiOverviewView/>}
    </div>
  );
}

function ApiOverviewView() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    (async () => {
      try { const { data } = await adminApi().get("/admin/b2b/api/overview"); setItems(data.items || []); }
      catch { toast.error("Fejl"); }
      setLoading(false);
    })();
  }, []);
  return (
    <div className="space-y-4" data-testid="admin-api-overview">
      <div>
        <h3 className="font-display font-bold text-2xl text-brand-dark">API & integrationer</h3>
        <p className="text-sm text-brand-text2">Oversigt over alle erhvervskunder med API-adgang, API-kald de sidste 100 og fejl-rate.</p>
      </div>
      {loading ? <Loader2 className="animate-spin" size={14}/> : (
        <div className="bg-white border border-brand overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr><th className="px-3 py-2 text-left">Kunde</th><th className="px-3 py-2">Aktive nøgler</th><th className="px-3 py-2">Total nøgler</th><th className="px-3 py-2">Seneste 100 kald</th><th className="px-3 py-2">Fejl-rate</th><th className="px-3 py-2">Sidste kald</th></tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const errorRate = it.recent_call_count ? Math.round((it.recent_error_count / it.recent_call_count) * 100) : 0;
                return (
                  <tr key={it.customer_id} className="border-t border-gray-100" data-testid={`admin-api-row-${it.customer_id}`}>
                    <td className="px-3 py-2">
                      {it.customer_name}
                      {it.customer_deleted && <span className="text-[10px] ml-2 bg-red-100 text-red-800 px-1.5 py-0.5">slettet</span>}
                    </td>
                    <td className="px-3 py-2 text-center">{it.active_keys}</td>
                    <td className="px-3 py-2 text-center text-xs text-brand-text2">{it.key_count}</td>
                    <td className="px-3 py-2 text-center">{it.recent_call_count}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs px-1.5 py-0.5 ${errorRate >= 20 ? "bg-red-100 text-red-800" : errorRate > 0 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {errorRate}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-brand-text2">{it.last_call_iso ? new Date(it.last_call_iso).toLocaleString("da-DK") : "—"}</td>
                  </tr>
                );
              })}
              {items.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-brand-text2">Ingen API-aktivitet endnu</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Customers ---------------- */
function CustomersView({ customers, refresh }) {
  const [editing, setEditing] = React.useState(null);
  const [portalCust, setPortalCust] = React.useState(null);

  const onDelete = async (id) => {
    if (!window.confirm("Slet/deaktiver denne erhvervskunde?")) return;
    try {
      const { data } = await adminApi().delete(`/admin/b2b/customers/${id}`);
      toast.success(data.deactivated ? "Markeret inaktiv (har pakkehistorik)" : "Kunde slettet");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sletning fejlede"); }
  };

  return (
    <div className="space-y-4" data-testid="b2b-customers-view">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h3 className="font-display font-bold text-2xl text-brand-dark">Erhvervskunder ({customers.length})</h3>
        <button onClick={() => setEditing({})} className="btn-primary" data-testid="b2b-customer-new-btn">
          <Plus size={16} /> Ny erhvervskunde
        </button>
      </div>

      {customers.length === 0 ? (
        <div className="bg-white border border-brand p-6 text-center text-brand-text2">Ingen erhvervskunder endnu.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {customers.map((c) => (
            <div key={c.id} className="bg-white border border-brand p-4" data-testid={`b2b-customer-card-${c.id}`}>
              <div className="flex justify-between items-start">
                <div className="min-w-0">
                  <div className="font-semibold text-brand-dark truncate">{c.company_name}</div>
                  <div className="text-xs text-brand-text2">CVR {c.cvr}</div>
                </div>
                {!c.active && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600">Inaktiv</span>}
              </div>
              <div className="mt-3 text-xs text-brand-text2 space-y-0.5">
                <div>{c.pickup_address}</div>
                <div>{c.pickup_postal_code} {c.pickup_city}</div>
                {c.pickup_zone_name && <div className="text-brand-dark">Zone: {c.pickup_zone_name}</div>}
                {c.contact_email && <div className="truncate">{c.contact_email}</div>}
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={() => setEditing(c)} className="text-xs underline text-brand-dark/80" data-testid={`b2b-customer-edit-${c.id}`}>Rediger</button>
                <button onClick={() => setPortalCust(c)} className="text-xs underline text-blue-700" data-testid={`b2b-customer-portal-users-${c.id}`}>Portal-brugere</button>
                <button onClick={() => onDelete(c.id)} className="text-xs underline text-red-600 ml-auto" data-testid={`b2b-customer-delete-${c.id}`}>Slet</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <CustomerEditModal customer={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {portalCust && <PortalUsersModal customer={portalCust} onClose={() => setPortalCust(null)} />}
    </div>
  );
}

function PortalUsersModal({ customer, onClose }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState({ email: "", name: "", role: "owner", method: "invite", temp_password: "" });
  const [lastInviteLink, setLastInviteLink] = React.useState("");
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get(`/admin/b2b/customers/${customer.id}/portal-users`);
      setItems(data.items || []);
    } catch { toast.error("Kunne ikke hente portal-brugere"); }
    setLoading(false);
  }, [customer.id]);

  React.useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form };
      if (form.method !== "temp_password") delete body.temp_password;
      const { data } = await adminApi().post(`/admin/b2b/customers/${customer.id}/portal-users/invite`, body);
      toast.success(form.method === "invite" ? "Invitation sendt" : "Bruger oprettet med midlertidigt kodeord");
      if (data.invite_link) setLastInviteLink(data.invite_link);
      setForm({ email: "", name: "", role: "owner", method: "invite", temp_password: "" });
      setAdding(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
  };

  const resend = async (uid) => {
    try {
      const { data } = await adminApi().post(`/admin/b2b/customers/${customer.id}/portal-users/${uid}/resend-invite`);
      toast.success("Invitation gen-sendt");
      if (data.invite_link) setLastInviteLink(data.invite_link);
    } catch { toast.error("Kunne ikke gen-sende"); }
  };

  const del = async (uid) => {
    if (!window.confirm("Slet denne portal-bruger?")) return;
    try {
      await adminApi().delete(`/admin/b2b/customers/${customer.id}/portal-users/${uid}`);
      toast.success("Slettet");
      load();
    } catch { toast.error("Sletning fejlede"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="b2b-portal-users-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-bold text-xl text-brand-dark">Portal-brugere</h3>
            <div className="text-sm text-brand-text2">{customer.company_name}</div>
          </div>
          <button onClick={onClose} className="text-brand-text2 hover:text-brand-dark">✕</button>
        </div>

        {lastInviteLink && (
          <div className="bg-blue-50 border border-blue-200 p-3 text-xs mb-3 break-all" data-testid="admin-portal-invite-link">
            <div className="font-semibold text-blue-900 mb-1">Invite-link (giv til kunden hvis email fejler):</div>
            <code className="text-blue-800">{lastInviteLink}</code>
            <button onClick={() => { navigator.clipboard.writeText(lastInviteLink); toast.success("Kopieret"); }} className="ml-2 text-blue-700 underline">Kopiér</button>
          </div>
        )}

        {loading ? <div className="text-sm text-brand-text2"><Loader2 className="animate-spin inline mr-2" size={14}/>Indlæser...</div> : (
          <table className="w-full text-sm mb-4">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Navn</th><th className="px-3 py-2 text-left">Rolle</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Sidste login</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-gray-100" data-testid={`admin-portal-user-row-${u.id}`}>
                  <td className="px-3 py-2">{u.email}</td>
                  <td className="px-3 py-2">{u.name || "—"}</td>
                  <td className="px-3 py-2"><span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800">{u.role}</span></td>
                  <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 ${u.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{u.status}</span></td>
                  <td className="px-3 py-2 text-xs text-brand-text2">{u.last_login_iso ? new Date(u.last_login_iso).toLocaleString("da-DK") : "Aldrig"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => resend(u.id)} className="text-xs underline text-blue-700 mr-2" data-testid={`admin-portal-user-resend-${u.id}`}>Gen-send</button>
                    <button onClick={() => del(u.id)} className="text-xs underline text-red-600" data-testid={`admin-portal-user-delete-${u.id}`}>Slet</button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-brand-text2">Ingen portal-brugere endnu.</td></tr>}
            </tbody>
          </table>
        )}

        {adding ? (
          <form onSubmit={submit} className="bg-brand-bg2 p-4 grid grid-cols-2 gap-3 text-sm" data-testid="admin-portal-user-form">
            <div className="col-span-2 font-semibold text-brand-dark">Tilføj portal-bruger</div>
            <div><label className="label-flat">Email *</label><input className="input-flat" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="admin-portal-user-email"/></div>
            <div><label className="label-flat">Navn</label><input className="input-flat" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="admin-portal-user-name"/></div>
            <div><label className="label-flat">Rolle</label>
              <select className="input-flat" value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="admin-portal-user-role">
                <option value="owner">Owner</option><option value="user">User</option>
              </select>
            </div>
            <div><label className="label-flat">Metode</label>
              <select className="input-flat" value={form.method} onChange={(e) => set("method", e.target.value)} data-testid="admin-portal-user-method">
                <option value="invite">Invite-email (anbefales)</option>
                <option value="temp_password">Admin-sat midlertidigt kodeord</option>
              </select>
            </div>
            {form.method === "temp_password" && (
              <div className="col-span-2"><label className="label-flat">Midlertidigt kodeord (min. 8 tegn — kunden skal skifte ved første login)</label>
                <input className="input-flat" type="text" required minLength={8} value={form.temp_password} onChange={(e) => set("temp_password", e.target.value)} data-testid="admin-portal-user-temp-pw"/>
              </div>
            )}
            <div className="col-span-2 flex gap-2">
              <button type="button" onClick={() => setAdding(false)} className="btn-secondary">Annullér</button>
              <button type="submit" className="btn-primary ml-auto" data-testid="admin-portal-user-submit">Opret</button>
            </div>
          </form>
        ) : (
          <button onClick={() => setAdding(true)} className="btn-primary" data-testid="admin-portal-user-add-btn"><Plus size={14}/> Tilføj bruger</button>
        )}
      </div>
    </div>
  );
}

function CustomerEditModal({ customer, onClose, onSaved }) {
  const isNew = !customer.id;
  const [c, setC] = React.useState({ active: true, ...customer });
  const [busy, setBusy] = React.useState(false);
  const [cvrBusy, setCvrBusy] = React.useState(false);
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const cvrLookup = async () => {
    const cvr = (c.cvr || "").replace(/\D/g, "");
    if (cvr.length !== 8) {
      toast.error("CVR skal være 8 cifre");
      return;
    }
    setCvrBusy(true);
    try {
      const { data } = await adminApi().get(`/admin/b2b/cvr-lookup/${cvr}`);
      if (!data.found) {
        if (data.error === "quota_exceeded") {
          toast.error("CVR-tjeneste midlertidigt nede (dagens kvote opbrugt) — indtast manuelt");
        } else if (data.error === "network") {
          toast.error("Netværksfejl mod CVR-tjenesten — indtast manuelt");
        } else {
          toast.error("CVR ikke fundet — indtast manuelt");
        }
        return;
      }
      const d = data.data;
      setC((prev) => ({
        ...prev,
        company_name: d.company_name || prev.company_name,
        pickup_address: d.address || prev.pickup_address,
        pickup_postal_code: d.postal_code || prev.pickup_postal_code,
        pickup_city: d.city || prev.pickup_city,
        contact_phone: prev.contact_phone || d.phone || "",
        contact_email: prev.contact_email || d.email || "",
        notes: (prev.notes || "") + (d.industry_desc ? `\nBranche: ${d.industry_desc} (${d.industry_code})` : ""),
      }));
      if (data.warning) toast.warning(data.warning);
      else toast.success(`Udfyldt fra CVR-register: ${d.company_name}`);
    } catch { toast.error("Lookup fejlede — indtast manuelt"); }
    finally { setCvrBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        await adminApi().post("/admin/b2b/customers", c);
        toast.success("Erhvervskunde oprettet");
      } else {
        await adminApi().patch(`/admin/b2b/customers/${c.id}`, c);
        toast.success("Opdateret");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gemning fejlede"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="b2b-customer-modal">
        <h3 className="font-display font-bold text-xl text-brand-dark">{isNew ? "Ny erhvervskunde" : "Rediger erhvervskunde"}</h3>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div><label className="label-flat">CVR * (8 cifre)</label>
            <div className="flex gap-2">
              <input className="input-flat flex-1" value={c.cvr || ""} onChange={(e) => set("cvr", e.target.value)} data-testid="b2b-cust-cvr" placeholder="25052943" />
              <button type="button" onClick={cvrLookup} disabled={cvrBusy} className="btn-secondary px-3 whitespace-nowrap" data-testid="b2b-cust-cvr-lookup" title="Slå op i CVR-registret">
                {cvrBusy ? <Loader2 className="animate-spin" size={14} /> : "Slå op"}
              </button>
            </div>
          </div>
          <div><label className="label-flat">Firmanavn *</label>
            <input className="input-flat" value={c.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="b2b-cust-name" /></div>
          <div><label className="label-flat">Kontaktperson</label>
            <input className="input-flat" value={c.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} data-testid="b2b-cust-contact" /></div>
          <div><label className="label-flat">Kontakt-email</label>
            <input type="email" className="input-flat" value={c.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="b2b-cust-email" /></div>
          <div><label className="label-flat">Telefon</label>
            <input className="input-flat" value={c.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} data-testid="b2b-cust-phone" /></div>
          <div><label className="label-flat">Faktura-email</label>
            <input type="email" className="input-flat" value={c.billing_email || ""} onChange={(e) => set("billing_email", e.target.value)} data-testid="b2b-cust-billing" /></div>
          <div className="col-span-2"><label className="label-flat">Afhentnings-adresse *</label>
            <input className="input-flat" value={c.pickup_address || ""} onChange={(e) => set("pickup_address", e.target.value)} data-testid="b2b-cust-pickup-addr" /></div>
          <div><label className="label-flat">Postnr *</label>
            <input className="input-flat" value={c.pickup_postal_code || ""} onChange={(e) => set("pickup_postal_code", e.target.value)} data-testid="b2b-cust-pickup-pc" /></div>
          <div><label className="label-flat">By *</label>
            <input className="input-flat" value={c.pickup_city || ""} onChange={(e) => set("pickup_city", e.target.value)} data-testid="b2b-cust-pickup-city" /></div>
          <div className="col-span-2"><label className="label-flat">Noter</label>
            <textarea className="input-flat min-h-[60px]" value={c.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          <label className="col-span-2 text-sm flex items-center gap-2">
            <input type="checkbox" checked={c.active !== false} onChange={(e) => set("active", e.target.checked)} /> Aktiv
          </label>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-secondary">Annullér</button>
          <button onClick={save} disabled={busy || !c.company_name || !c.cvr || !c.pickup_address} className="btn-primary" data-testid="b2b-cust-save">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Gem
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Agreements ---------------- */
function AgreementsView({ customers }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/b2b/agreements");
      setItems(data.items || []);
    } catch { toast.error("Kunne ikke hente aftaler"); }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const activate = async (id) => {
    try {
      await adminApi().patch(`/admin/b2b/agreements/${id}`, { status: "active" });
      toast.success("Aftale aktiveret");
      load();
    } catch { toast.error("Aktivering fejlede"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Slet denne aftale?")) return;
    try {
      await adminApi().delete(`/admin/b2b/agreements/${id}`);
      toast.success("Slettet");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sletning fejlede"); }
  };

  const downloadPdf = async (id, companyName) => {
    try {
      const tk = localStorage.getItem("ema_admin_token");
      const res = await fetch(`${API}/admin/b2b/agreements/${id}/pdf`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) throw new Error("PDF fejl");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (companyName || "kunde").replace(/\s+/g, "_");
      a.download = `ema-erhvervsaftale-${safe}-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloadet");
    } catch { toast.error("Kunne ikke hente PDF"); }
  };

  if (loading) return <div className="text-brand-text2"><Loader2 className="animate-spin inline mr-2" size={16} /> Indlæser...</div>;

  const statusBadge = (s) => {
    const colors = { draft: "bg-gray-100 text-gray-700", active: "bg-green-100 text-green-700", expired: "bg-yellow-100 text-yellow-800", cancelled: "bg-red-100 text-red-700" };
    return <span className={`text-xs px-2 py-0.5 ${colors[s] || colors.draft}`}>{s}</span>;
  };

  return (
    <div className="space-y-4" data-testid="b2b-agreements-view">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h3 className="font-display font-bold text-2xl text-brand-dark">Aftaler ({items.length})</h3>
        <button onClick={() => setEditing({ status: "draft" })} className="btn-primary" data-testid="b2b-agreement-new-btn" disabled={customers.length === 0}>
          <Plus size={16} /> Ny aftale
        </button>
      </div>
      {customers.length === 0 && <div className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">Opret en erhvervskunde først, før du opretter aftaler.</div>}
      {items.length === 0 ? (
        <div className="bg-white border border-brand p-6 text-center text-brand-text2">Ingen aftaler endnu.</div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="bg-white border border-brand p-4 flex flex-wrap gap-4 items-center" data-testid={`b2b-agreement-card-${a.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold text-brand-dark">{a.business_customer_name}</div>
                <div className="text-xs text-brand-text2">{a.agreement_period_start} → {a.agreement_period_end || "Åben"} · Forventet vol: {a.expected_monthly_volume}/md</div>
              </div>
              <div className="text-xs text-brand-text2">
                <div>SameDay: {Object.values(a.same_day_tier_prices || {}).join(" / ")} kr</div>
                <div>NextDay: {Object.values(a.next_day_tier_prices || {}).join(" / ")} kr</div>
              </div>
              <div>{statusBadge(a.status)}</div>
              <div className="flex gap-2">
                <button onClick={() => downloadPdf(a.id, a.business_customer_name)} className="text-xs underline text-blue-700 inline-flex items-center gap-1" data-testid={`b2b-agreement-pdf-${a.id}`}>
                  <FileDown size={12}/> PDF
                </button>
                {a.status === "draft" && <button onClick={() => activate(a.id)} className="text-xs underline text-green-700" data-testid={`b2b-agreement-activate-${a.id}`}>Aktivér</button>}
                <button onClick={() => onDelete(a.id)} className="text-xs underline text-red-600" data-testid={`b2b-agreement-delete-${a.id}`}>Slet</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <AgreementCreateModal customers={customers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AgreementCreateModal({ customers, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [a, setA] = React.useState({
    business_customer_id: customers[0]?.id || "",
    agreement_period_start: today,
    agreement_period_end: "",
    expected_monthly_volume: 100,
    minimum_pickup_fee_dkk: 499,
    energy_surcharge_pct: 8,
    failed_delivery_fee_dkk: 99,
    wrong_address_fee_dkk: 99,
    extra_handling_fee_dkk: 49,
    payment_terms_days: 8,
    invoice_frequency: "monthly",
    selected_products: ["same_day_b2b", "next_day_b2b"],
    status: "draft",
  });
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...a };
      if (!body.agreement_period_end) delete body.agreement_period_end;
      await adminApi().post("/admin/b2b/agreements", body);
      toast.success("Aftale oprettet");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Oprettelse fejlede"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="b2b-agreement-modal">
        <h3 className="font-display font-bold text-xl text-brand-dark">Ny aftale</h3>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="col-span-2"><label className="label-flat">Erhvervskunde *</label>
            <select className="input-flat" value={a.business_customer_id} onChange={(e) => set("business_customer_id", e.target.value)} data-testid="b2b-agr-customer">
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name} (CVR {c.cvr})</option>)}
            </select></div>
          <div><label className="label-flat">Start-dato</label>
            <input type="date" className="input-flat" value={a.agreement_period_start} onChange={(e) => set("agreement_period_start", e.target.value)} data-testid="b2b-agr-start" /></div>
          <div><label className="label-flat">Slut-dato (valgfri)</label>
            <input type="date" className="input-flat" value={a.agreement_period_end} onChange={(e) => set("agreement_period_end", e.target.value)} data-testid="b2b-agr-end" /></div>
          <div><label className="label-flat">Forventet månedlig volumen</label>
            <input type="number" className="input-flat" value={a.expected_monthly_volume} onChange={(e) => set("expected_monthly_volume", parseInt(e.target.value) || 0)} /></div>
          <div><label className="label-flat">Min. afhentnings-gebyr (kr)</label>
            <input type="number" className="input-flat" value={a.minimum_pickup_fee_dkk} onChange={(e) => set("minimum_pickup_fee_dkk", parseFloat(e.target.value) || 0)} /></div>
          <div><label className="label-flat">Energi-tillæg (%)</label>
            <input type="number" step="0.5" className="input-flat" value={a.energy_surcharge_pct} onChange={(e) => set("energy_surcharge_pct", parseFloat(e.target.value) || 0)} /></div>
          <div><label className="label-flat">Betalingsfrist (dage)</label>
            <input type="number" className="input-flat" value={a.payment_terms_days} onChange={(e) => set("payment_terms_days", parseInt(e.target.value) || 0)} /></div>
          <div><label className="label-flat">Fejllevering-gebyr (kr)</label>
            <input type="number" className="input-flat" value={a.failed_delivery_fee_dkk} onChange={(e) => set("failed_delivery_fee_dkk", parseFloat(e.target.value) || 0)} /></div>
          <div><label className="label-flat">Forkert adresse-gebyr (kr)</label>
            <input type="number" className="input-flat" value={a.wrong_address_fee_dkk} onChange={(e) => set("wrong_address_fee_dkk", parseFloat(e.target.value) || 0)} /></div>
          <div><label className="label-flat">Faktura-frekvens</label>
            <select className="input-flat" value={a.invoice_frequency} onChange={(e) => set("invoice_frequency", e.target.value)}>
              <option value="weekly">Ugentlig</option>
              <option value="biweekly">Hver 14. dag</option>
              <option value="monthly">Månedlig</option>
            </select></div>
          <div className="col-span-2 bg-brand-bg2 p-3 text-xs text-brand-dark/80">
            Standard tier-priser fra produkt-spec bruges automatisk (Same Day: 119/89/79/69 kr, Next Day: 89/69/59/49 kr). Kan tilpasses senere.
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-secondary">Annullér</button>
          <button onClick={save} disabled={busy || !a.business_customer_id} className="btn-primary" data-testid="b2b-agr-save">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Opret som kladde
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Parcels ---------------- */
function ParcelsView({ customers }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filterCust, setFilterCust] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = filterCust ? `?business_customer_id=${filterCust}` : "";
      const { data } = await adminApi().get(`/admin/b2b/parcels${params}`);
      setItems(data.items || []);
    } catch { toast.error("Kunne ikke hente pakker"); }
    setLoading(false);
  }, [filterCust]);

  React.useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    if (!window.confirm("Annullér denne pakke?")) return;
    try {
      await adminApi().post(`/admin/b2b/parcels/${id}/cancel`);
      toast.success("Annulleret");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke annullere"); }
  };

  return (
    <div className="space-y-4" data-testid="b2b-parcels-view">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h3 className="font-display font-bold text-2xl text-brand-dark">Pakker ({items.length})</h3>
        <div className="flex gap-2 items-center">
          <select className="input-flat" value={filterCust} onChange={(e) => setFilterCust(e.target.value)} data-testid="b2b-parcels-filter">
            <option value="">Alle kunder</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
          <button onClick={() => setCreating(true)} className="btn-primary" data-testid="b2b-parcel-new-btn" disabled={customers.filter((c) => c.active).length === 0}>
            <Plus size={16} /> Ny pakke
          </button>
        </div>
      </div>

      {loading ? <div className="text-brand-text2"><Loader2 className="animate-spin inline mr-2" size={16} /> Indlæser...</div> :
       items.length === 0 ? <div className="bg-white border border-brand p-6 text-center text-brand-text2">Ingen pakker endnu.</div> : (
        <div className="overflow-x-auto bg-white border border-brand">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Nr.</th>
                <th className="px-3 py-2 text-left">Kunde</th>
                <th className="px-3 py-2 text-left">Modtager</th>
                <th className="px-3 py-2 text-left">Zoner</th>
                <th className="px-3 py-2 text-right">Vægt</th>
                <th className="px-3 py-2 text-right">Vol.</th>
                <th className="px-3 py-2 text-right">Pris</th>
                <th className="px-3 py-2 text-left">Produkt</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-brand-bg2" data-testid={`b2b-parcel-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{p.parcel_number}</td>
                  <td className="px-3 py-2 truncate max-w-[140px]">{p.business_customer_name}</td>
                  <td className="px-3 py-2 truncate max-w-[160px]">{p.recipient_name}<br/><span className="text-xs text-brand-text2">{p.delivery_postal_code} {p.delivery_city}</span></td>
                  <td className="px-3 py-2 text-xs">{p.pickup_zone_name} → {p.delivery_zone_name}</td>
                  <td className="px-3 py-2 text-right">{p.weight_kg} kg</td>
                  <td className="px-3 py-2 text-right">{p.volume_l} L</td>
                  <td className="px-3 py-2 text-right font-semibold">{p.price?.total_dkk?.toFixed(2)} kr</td>
                  <td className="px-3 py-2 text-xs">{p.product === "same_day_b2b" ? "Same Day" : "Next Day"}</td>
                  <td className="px-3 py-2"><StatusBadge s={p.status} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <a href={`/track/${p.id}`} target="_blank" rel="noopener noreferrer" className="text-xs underline text-blue-700 mr-2" data-testid={`b2b-parcel-track-${p.id}`}>Track</a>
                    <button onClick={async () => {
                      const tk = localStorage.getItem("ema_admin_token");
                      const res = await fetch(`${API}/admin/b2b/shipments/${p.id}/label.pdf?fmt=thermal`, { headers: { Authorization: `Bearer ${tk}` } });
                      if (res.ok) { const blob = await res.blob(); window.open(URL.createObjectURL(blob), "_blank"); }
                      else { toast.error("Label fejl"); }
                    }} className="text-xs underline text-blue-700 mr-2" data-testid={`b2b-parcel-label-${p.id}`}>Label</button>
                    {["created", "ready_for_pickup"].includes(p.status) && (
                      <button onClick={() => cancel(p.id)} className="text-xs underline text-red-600" data-testid={`b2b-parcel-cancel-${p.id}`}>Annullér</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <ParcelCreateModal customers={customers.filter((c) => c.active)} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    created: "bg-gray-100 text-gray-700",
    ready_for_pickup: "bg-blue-100 text-blue-700",
    picked_up: "bg-indigo-100 text-indigo-700",
    at_hub: "bg-purple-100 text-purple-700",
    sorted: "bg-purple-200 text-purple-800",
    out_for_delivery: "bg-amber-100 text-amber-800",
    delivered: "bg-green-100 text-green-700",
    failed_delivery: "bg-orange-100 text-orange-800",
    returned: "bg-red-100 text-red-700",
    cancelled: "bg-gray-200 text-gray-600",
  };
  return <span className={`text-xs px-2 py-0.5 ${map[s] || map.created}`}>{s}</span>;
}

function ParcelCreateModal({ customers, onClose, onSaved }) {
  const [p, setP] = React.useState({
    business_customer_id: customers[0]?.id || "",
    product: "next_day_b2b",
    colli_count: 1,
    length_cm: 30,
    width_cm: 20,
    height_cm: 15,
    weight_kg: 1.5,
    delivery_address: "",
    delivery_postal_code: "",
    delivery_city: "",
    recipient_name: "",
    recipient_phone: "",
  });
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi().post("/admin/b2b/parcels", p);
      toast.success(`Pakke ${data.parcel_number} oprettet (${data.price?.total_dkk?.toFixed(2)} kr)`);
      onSaved();
    } catch (e) {
      const errs = e?.response?.data?.detail;
      if (errs && typeof errs === "object" && errs.validation_errors) {
        toast.error(errs.validation_errors.join(" · "));
      } else {
        toast.error(typeof errs === "string" ? errs : "Oprettelse fejlede");
      }
    } finally { setBusy(false); }
  };

  const volPer = ((p.length_cm * p.width_cm * p.height_cm) / 1000).toFixed(1);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="b2b-parcel-modal">
        <h3 className="font-display font-bold text-xl text-brand-dark">Ny pakke</h3>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="col-span-2"><label className="label-flat">Erhvervskunde *</label>
            <select className="input-flat" value={p.business_customer_id} onChange={(e) => set("business_customer_id", e.target.value)} data-testid="b2b-parcel-customer">
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select></div>
          <div><label className="label-flat">Produkt *</label>
            <select className="input-flat" value={p.product} onChange={(e) => set("product", e.target.value)} data-testid="b2b-parcel-product">
              <option value="same_day_b2b">EMA Same Day Business</option>
              <option value="next_day_b2b">EMA Next Day Business</option>
            </select></div>
          <div><label className="label-flat">Antal colli</label>
            <input type="number" min="1" className="input-flat" value={p.colli_count} onChange={(e) => set("colli_count", parseInt(e.target.value) || 1)} data-testid="b2b-parcel-colli" /></div>
          <div><label className="label-flat">Længde (cm)</label>
            <input type="number" className="input-flat" value={p.length_cm} onChange={(e) => set("length_cm", parseFloat(e.target.value) || 0)} data-testid="b2b-parcel-length" /></div>
          <div><label className="label-flat">Bredde (cm)</label>
            <input type="number" className="input-flat" value={p.width_cm} onChange={(e) => set("width_cm", parseFloat(e.target.value) || 0)} data-testid="b2b-parcel-width" /></div>
          <div><label className="label-flat">Højde (cm)</label>
            <input type="number" className="input-flat" value={p.height_cm} onChange={(e) => set("height_cm", parseFloat(e.target.value) || 0)} data-testid="b2b-parcel-height" /></div>
          <div><label className="label-flat">Vægt (kg) — max 11 kg</label>
            <input type="number" step="0.1" max="11" className="input-flat" value={p.weight_kg} onChange={(e) => set("weight_kg", parseFloat(e.target.value) || 0)} data-testid="b2b-parcel-weight" /></div>
          <div className="col-span-2 text-xs text-brand-text2">Volumen per colli: <b>{volPer} L</b></div>
          <div className="col-span-2"><label className="label-flat">Modtagernavn *</label>
            <input className="input-flat" value={p.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} data-testid="b2b-parcel-recipient" /></div>
          <div><label className="label-flat">Modtager-telefon</label>
            <input className="input-flat" value={p.recipient_phone} onChange={(e) => set("recipient_phone", e.target.value)} /></div>
          <div><label className="label-flat">Modtager-email</label>
            <input type="email" className="input-flat" value={p.recipient_email || ""} onChange={(e) => set("recipient_email", e.target.value)} /></div>
          <div className="col-span-2"><label className="label-flat">Leveringsadresse *</label>
            <input className="input-flat" value={p.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} data-testid="b2b-parcel-deliv-addr" /></div>
          <div><label className="label-flat">Postnr *</label>
            <input className="input-flat" value={p.delivery_postal_code} onChange={(e) => set("delivery_postal_code", e.target.value)} data-testid="b2b-parcel-deliv-pc" /></div>
          <div><label className="label-flat">By *</label>
            <input className="input-flat" value={p.delivery_city} onChange={(e) => set("delivery_city", e.target.value)} data-testid="b2b-parcel-deliv-city" /></div>
          <div className="col-span-2"><label className="label-flat">Note</label>
            <textarea className="input-flat min-h-[60px]" value={p.delivery_note || ""} onChange={(e) => set("delivery_note", e.target.value)} /></div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-secondary">Annullér</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="b2b-parcel-save">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Opret pakke
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Quote calculator ---------------- */
function QuoteView({ customers }) {
  const [q, setQ] = React.useState({ product: "next_day_b2b", colli_count: 1, monthly_parcels_override: 25, manual_label: false, business_customer_id: "" });
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const calc = async () => {
    setBusy(true);
    try {
      const body = { ...q };
      if (!body.business_customer_id) delete body.business_customer_id;
      const { data } = await adminApi().post("/admin/b2b/quote", body);
      setResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Beregning fejlede"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="b2b-quote-view">
      <h3 className="font-display font-bold text-2xl text-brand-dark">Pris-beregner</h3>
      <p className="text-sm text-brand-text2">Beregn live pris baseret på produkt, antal colli og månedlig volumen. Brug en eksisterende kunde for at få aftalens overrides.</p>
      <div className="bg-white border border-brand p-5 grid grid-cols-2 gap-3 max-w-2xl">
        <div className="col-span-2"><label className="label-flat">Erhvervskunde (valgfri)</label>
          <select className="input-flat" value={q.business_customer_id} onChange={(e) => setQ({ ...q, business_customer_id: e.target.value })} data-testid="quote-customer">
            <option value="">— Ingen (brug standard-priser) —</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select></div>
        <div><label className="label-flat">Produkt</label>
          <select className="input-flat" value={q.product} onChange={(e) => setQ({ ...q, product: e.target.value })} data-testid="quote-product">
            <option value="same_day_b2b">Same Day Business</option>
            <option value="next_day_b2b">Next Day Business</option>
          </select></div>
        <div><label className="label-flat">Antal colli</label>
          <input type="number" min="1" className="input-flat" value={q.colli_count} onChange={(e) => setQ({ ...q, colli_count: parseInt(e.target.value) || 1 })} data-testid="quote-colli" /></div>
        <div><label className="label-flat">Månedlig volumen (override)</label>
          <input type="number" min="0" className="input-flat" value={q.monthly_parcels_override} onChange={(e) => setQ({ ...q, monthly_parcels_override: parseInt(e.target.value) || 0 })} data-testid="quote-monthly" /></div>
        <label className="text-sm flex items-center gap-2 col-span-2">
          <input type="checkbox" checked={q.manual_label} onChange={(e) => setQ({ ...q, manual_label: e.target.checked })} data-testid="quote-manual-label" /> Manuel label-fee (5 kr/colli)
        </label>
        <button onClick={calc} disabled={busy} className="col-span-2 btn-primary" data-testid="quote-calc-btn">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Calculator size={16} />} Beregn pris
        </button>
      </div>

      {result && (
        <div className="bg-white border border-brand p-5 max-w-2xl space-y-1" data-testid="quote-result">
          <div className="grid grid-cols-2 gap-x-4 text-sm">
            <div className="text-brand-text2">Tier:</div><div className="text-brand-dark">{result.tier}</div>
            <div className="text-brand-text2">Stk-pris:</div><div className="text-brand-dark">{result.unit_price_dkk?.toFixed(2)} kr</div>
            <div className="text-brand-text2">Subtotal:</div><div className="text-brand-dark">{result.subtotal_dkk?.toFixed(2)} kr</div>
            {result.manual_label_fee_dkk > 0 && <><div className="text-brand-text2">Manuel label-fee:</div><div className="text-brand-dark">{result.manual_label_fee_dkk?.toFixed(2)} kr</div></>}
            <div className="text-brand-text2">Energi-tillæg ({result.energy_surcharge_pct}%):</div><div className="text-brand-dark">{result.energy_surcharge_dkk?.toFixed(2)} kr</div>
            <div className="text-brand-text2 font-semibold border-t border-brand-bg2 pt-2 mt-2">Total (ex moms):</div>
            <div className="text-brand-red font-bold text-lg border-t border-brand-bg2 pt-2 mt-2">{result.total_dkk?.toFixed(2)} kr</div>
          </div>
          <div className="text-xs text-brand-text2 pt-3">Pris-kilde: {result.price_source === "agreement" ? "Aftale-override" : "Standard-tier"} · Månedlig volumen brugt: {result.monthly_parcels}</div>
        </div>
      )}
    </div>
  );
}


const todayDk = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function ManifestsView({ customers }) {
  const [date, setDate] = React.useState(todayDk());
  const [filterCust, setFilterCust] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = { date };
      if (filterCust) params.customer_id = filterCust;
      const { data } = await adminApi().get("/admin/b2b/pickup-manifests", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente manifests");
    } finally {
      setLoading(false);
    }
  }, [date, filterCust]);

  React.useEffect(() => { load(); }, [load]);

  const downloadOne = async (cid, locId, name) => {
    try {
      const tk = localStorage.getItem("ema_admin_token");
      const res = await fetch(`${API}/admin/b2b/pickup-manifests/${cid}/${locId}/${date}/manifest.pdf`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (!res.ok) throw new Error("Download fejlede");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `manifest_${date}_${(name || "pickup").replace(/\s+/g, "_")}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Download fejlede");
    }
  };

  const downloadAll = async () => {
    try {
      const tk = localStorage.getItem("ema_admin_token");
      const res = await fetch(`${API}/admin/b2b/pickup-manifests/today.pdf?date=${date}`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "Bulk-download fejlede");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (w) {
        setTimeout(() => { try { w.print(); } catch { /* print may be blocked */ } }, 1500);
      }
    } catch (e) {
      toast.error(e.message || "Bulk-download fejlede");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-manifests-view">
      <div className="flex flex-wrap gap-3 items-end bg-white border border-brand p-4">
        <div>
          <label className="label-flat">Dato</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-flat py-1.5" data-testid="admin-manifest-date"/>
        </div>
        <div>
          <label className="label-flat">Kunde-filter</label>
          <select className="input-flat py-1.5" value={filterCust} onChange={(e) => setFilterCust(e.target.value)} data-testid="admin-manifest-customer">
            <option value="">Alle kunder</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white">Opdater</button>
        <button onClick={downloadAll} className="px-3 py-1.5 text-xs font-semibold text-white bg-brand-red hover:opacity-90 inline-flex items-center gap-1 ml-auto" data-testid="admin-manifest-print-all">
          <Printer size={12}/> Print alle for {date}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-brand-text2 inline-flex items-center gap-2"><Loader2 className="animate-spin"/> Indlæser...</div>
      ) : items.length === 0 ? (
        <div className="border border-brand bg-white p-8 text-center text-brand-text2">Ingen pickup-manifests for valgte filter</div>
      ) : (
        <div className="border border-brand bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr>
                <th className="px-3 py-2 text-left">Manifest #</th>
                <th className="px-3 py-2 text-left">Kunde</th>
                <th className="px-3 py-2 text-left">Pickup-lokation</th>
                <th className="px-3 py-2 text-right">Forsendelser</th>
                <th className="px-3 py-2 text-right">Kolli</th>
                <th className="px-3 py-2 text-right">Vægt</th>
                <th className="px-3 py-2 text-right">SD/ND</th>
                <th className="px-3 py-2 text-left">Zoner</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={`${m.customer_id}-${m.pickup_location_id}`} className="border-t border-brand" data-testid={`admin-manifest-row-${m.pickup_location_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{m.manifest_number}</td>
                  <td className="px-3 py-2">{m.customer_name}</td>
                  <td className="px-3 py-2">
                    <div className="text-brand-dark font-semibold">{m.pickup_location_name}</div>
                    <div className="text-xs text-brand-text2">{m.pickup_postal_code} {m.pickup_city}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{m.aggregate?.total_shipments}</td>
                  <td className="px-3 py-2 text-right">{m.aggregate?.total_colli}</td>
                  <td className="px-3 py-2 text-right">{(m.aggregate?.total_weight_kg || 0).toFixed(1)} kg</td>
                  <td className="px-3 py-2 text-right text-xs">
                    <span className="text-brand-red font-semibold">{m.aggregate?.same_day_count || 0}</span> /
                    <span className="text-brand-dark font-semibold ml-1">{m.aggregate?.next_day_count || 0}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{(m.zones || []).join(", ") || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => downloadOne(m.customer_id, m.pickup_location_id, m.pickup_location_name)}
                        className="inline-flex items-center gap-1 text-xs text-brand-red hover:text-brand-dark"
                        data-testid={`admin-manifest-download-${m.pickup_location_id}`}>
                        <Download size={12}/> PDF
                      </button>
                      <LockToggleBtn cid={m.customer_id} locId={m.pickup_location_id} date={date}/>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LockToggleBtn({ cid, locId, date }) {
  const [locked, setLocked] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const loadStatus = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get(`/admin/b2b/pickup-manifests/${cid}/${locId}/${date}/lock-audit`);
      setLocked(data?.current_lock?.locked || false);
    } catch { setLocked(false); }
  }, [cid, locId, date]);
  React.useEffect(() => { loadStatus(); }, [loadStatus]);

  const toggle = async () => {
    setBusy(true);
    try {
      const path = locked ? "unlock" : "lock";
      await adminApi().post(`/admin/b2b/pickup-manifests/${cid}/${locId}/${date}/${path}`);
      setLocked(!locked);
      toast.success(locked ? "Manifest låst op" : "Manifest låst");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lock/unlock fejlede");
    } finally { setBusy(false); }
  };

  return (
    <button onClick={toggle} disabled={busy} className={`inline-flex items-center gap-1 text-xs hover:opacity-80 ${locked ? "text-red-700" : "text-brand-text2"}`} data-testid={`admin-manifest-lock-${locId}`}>
      {locked ? <Lock size={12}/> : <Unlock size={12}/>}
      {locked ? "Låst" : "Låsop"}
    </button>
  );
}


const REASON_OPTS = [
  ["recipient_not_home", "Modtager ikke hjemme"],
  ["no_access", "Ingen adgang"],
  ["wrong_address", "Forkert adresse (gebyr)"],
  ["wrong_phone", "Forkert telefon (gebyr)"],
  ["recipient_refused", "Modtager afviste"],
  ["unsafe_location", "Usikker lokation"],
  ["damaged_parcel", "Skadet pakke"],
  ["missing_parcel", "Manglende pakke"],
  ["driver_could_not_locate_address", "Adresse ikke fundet"],
  ["cancelled_by_recipient", "Annulleret af modtager (gebyr)"],
  ["weather_or_traffic", "Vejr/trafik"],
  ["other", "Andet"],
];

const NEXT_ACTION_OPTS = [
  ["redelivery", "Genlevering"],
  ["return_to_sender", "Returnér til afsender"],
  ["manual_review", "Manual review"],
  ["charge_failed_delivery_fee", "Opkræv gebyr"],
  ["waive_fee", "Eftergiv gebyr"],
  ["contact_customer", "Kontakt kunde"],
  ["contact_recipient", "Kontakt modtager"],
];

function FailedReturnsView({ customers }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [filterCust, setFilterCust] = React.useState("");
  const [filterReason, setFilterReason] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");
  const [modal, setModal] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCust) params.customer_id = filterCust;
      if (filterReason) params.reason_code = filterReason;
      if (filterStatus) params.status_filter = filterStatus;
      const { data } = await adminApi().get("/admin/b2b/shipments/failed-deliveries", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente cases");
    } finally {
      setLoading(false);
    }
  }, [filterCust, filterReason, filterStatus]);

  React.useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const header = ["shipment_number", "customer_name", "customer_reference", "status", "reason_code", "attempts", "fee_dkk", "recipient", "postal", "city", "failed_at"];
    const rows = items.map((s) => [
      s.shipment_number, s.business_customer_name, s.customer_reference || "",
      s.status, s.failed_delivery_reason_code || "",
      s.failed_attempts_count || 0,
      s.failed_delivery_fee_dkk || 0,
      s.recipient_name, s.delivery_postal_code, s.delivery_city,
      s.failed_delivery_at_iso || "",
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `failed_deliveries_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="admin-failed-view">
      <div className="flex flex-wrap gap-3 items-end bg-white border border-brand p-4">
        <div>
          <label className="label-flat">Kunde</label>
          <select className="input-flat py-1.5" value={filterCust} onChange={(e) => setFilterCust(e.target.value)} data-testid="admin-fr-customer">
            <option value="">Alle</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-flat">Status</label>
          <select className="input-flat py-1.5" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="admin-fr-status">
            <option value="">Alle</option>
            <option value="failed_delivery">Mislykket</option>
            <option value="redelivery_requested">Genlevering anmodet</option>
            <option value="manual_review">Manual review</option>
            <option value="returned">Returneret</option>
          </select>
        </div>
        <div>
          <label className="label-flat">Årsag</label>
          <select className="input-flat py-1.5" value={filterReason} onChange={(e) => setFilterReason(e.target.value)} data-testid="admin-fr-reason">
            <option value="">Alle</option>
            {REASON_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white">Opdater</button>
        <button onClick={exportCsv} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white ml-auto inline-flex items-center gap-1" data-testid="admin-fr-export"><Download size={12}/> Eksportér CSV</button>
      </div>

      {loading ? <div className="text-center py-6 text-brand-text2"><Loader2 className="inline animate-spin mr-2"/>Indlæser...</div>
      : items.length === 0 ? <div className="border border-brand bg-white p-6 text-center text-brand-text2">Ingen åbne fejl/return-cases</div>
      : (
        <div className="border border-brand bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr>
                <th className="px-2 py-2 text-left">Shipment</th>
                <th className="px-2 py-2 text-left">Kunde</th>
                <th className="px-2 py-2 text-left">Modtager / postnr</th>
                <th className="px-2 py-2 text-left">Status</th>
                <th className="px-2 py-2 text-left">Årsag</th>
                <th className="px-2 py-2 text-right">Forsøg</th>
                <th className="px-2 py-2 text-right">Gebyr</th>
                <th className="px-2 py-2 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-brand" data-testid={`admin-fr-row-${s.id}`}>
                  <td className="px-2 py-2 font-mono text-xs">{s.shipment_number}</td>
                  <td className="px-2 py-2 text-xs">{s.business_customer_name}</td>
                  <td className="px-2 py-2 text-xs">
                    <div>{s.recipient_name}</div>
                    <div className="text-brand-text2">{s.delivery_postal_code} {s.delivery_city}</div>
                  </td>
                  <td className="px-2 py-2 text-xs">{s.status}</td>
                  <td className="px-2 py-2 text-xs">{s.failed_delivery_reason_code || "—"}</td>
                  <td className="px-2 py-2 text-right text-xs">{s.failed_attempts_count || 0}</td>
                  <td className="px-2 py-2 text-right text-xs">{s.failed_delivery_fee_charged ? `${(s.failed_delivery_fee_dkk || 0).toFixed(0)} kr` : "—"}</td>
                  <td className="px-2 py-2 text-right">
                    <div className="flex justify-end gap-1 flex-wrap">
                      {["out_for_delivery", "at_hub", "sorted"].includes(s.status) && (
                        <button onClick={() => setModal({ kind: "fail", shipment: s })} className="px-2 py-1 text-xs font-semibold text-white bg-red-600 hover:bg-red-700" data-testid={`admin-fr-fail-${s.id}`}>Mark fail</button>
                      )}
                      {s.status === "redelivery_requested" && (
                        <button onClick={() => setModal({ kind: "redeliv", shipment: s })} className="px-2 py-1 text-xs font-semibold text-white bg-brand-red hover:opacity-90" data-testid={`admin-fr-redeliv-${s.id}`}>Godkend/afvis</button>
                      )}
                      {["failed_delivery", "manual_review", "redelivery_requested"].includes(s.status) && (
                        <button onClick={() => setModal({ kind: "return", shipment: s })} className="px-2 py-1 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white" data-testid={`admin-fr-return-${s.id}`}>Returnér</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && <FailModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}/>}
    </div>
  );
}

function FailModal({ modal, onClose, onSaved }) {
  const { kind, shipment } = modal;
  const [reason, setReason] = React.useState("recipient_not_home");
  const [comment, setComment] = React.useState("");
  const [nextAction, setNextAction] = React.useState("");
  const [feeCharged, setFeeCharged] = React.useState("auto");
  const [feeAmount, setFeeAmount] = React.useState("");
  const [returnReason, setReturnReason] = React.useState("");
  const [adminNote, setAdminNote] = React.useState("");
  const [approve, setApprove] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      if (kind === "fail") {
        const body = { reason_code: reason };
        if (comment) body.driver_comment = comment;
        if (nextAction) body.next_action = nextAction;
        if (feeCharged !== "auto") body.fee_charged = feeCharged === "yes";
        if (feeAmount) body.fee_amount_dkk = Number(feeAmount);
        await adminApi().post(`/admin/b2b/shipments/${shipment.id}/fail`, body);
        toast.success("Fail registreret");
      } else if (kind === "return") {
        const body = { return_reason: returnReason || undefined, admin_note: adminNote || undefined };
        if (feeCharged !== "auto") body.fee_charged = feeCharged === "yes";
        if (feeAmount) body.fee_amount_dkk = Number(feeAmount);
        await adminApi().post(`/admin/b2b/shipments/${shipment.id}/return`, body);
        toast.success("Markeret returneret");
      } else if (kind === "redeliv") {
        const body = { approve, admin_note: adminNote || undefined };
        if (feeAmount) body.fee_amount_dkk = Number(feeAmount);
        await adminApi().post(`/admin/b2b/shipments/${shipment.id}/redelivery-approve`, body);
        toast.success(approve ? "Genlevering godkendt" : "Genlevering afvist");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Handling fejlede");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-fr-modal">
      <div className="bg-white border border-brand max-w-lg w-full">
        <div className="border-b border-brand px-5 py-3 flex justify-between items-center">
          <h3 className="font-display font-bold text-lg text-brand-dark">
            {kind === "fail" && "Registrér mislykket levering"}
            {kind === "return" && "Marker returneret"}
            {kind === "redeliv" && "Genlevering — beslutning"}
          </h3>
          <button onClick={onClose} className="text-2xl text-brand-text2 hover:text-brand-dark">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-brand-text2">{shipment.shipment_number} · {shipment.business_customer_name}</div>
          {kind === "fail" && (
            <>
              <div>
                <label className="label-flat">Årsag *</label>
                <select className="input-flat py-1.5 w-full" value={reason} onChange={(e) => setReason(e.target.value)}>
                  {REASON_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="label-flat">Chauffør-kommentar</label>
                <textarea className="input-flat py-1.5 w-full" rows={2} value={comment} onChange={(e) => setComment(e.target.value)}/>
              </div>
              <div>
                <label className="label-flat">Næste handling</label>
                <select className="input-flat py-1.5 w-full" value={nextAction} onChange={(e) => setNextAction(e.target.value)}>
                  <option value="">(ikke valgt)</option>
                  {NEXT_ACTION_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </>
          )}
          {kind === "return" && (
            <>
              <div>
                <label className="label-flat">Return-årsag</label>
                <input className="input-flat py-1.5 w-full" value={returnReason} onChange={(e) => setReturnReason(e.target.value)}/>
              </div>
              <div>
                <label className="label-flat">Admin-note</label>
                <textarea className="input-flat py-1.5 w-full" rows={2} value={adminNote} onChange={(e) => setAdminNote(e.target.value)}/>
              </div>
            </>
          )}
          {kind === "redeliv" && (
            <>
              <div className="flex gap-3 items-center">
                <label className="inline-flex items-center gap-1 text-sm"><input type="radio" checked={approve} onChange={() => setApprove(true)}/> Godkend</label>
                <label className="inline-flex items-center gap-1 text-sm"><input type="radio" checked={!approve} onChange={() => setApprove(false)}/> Afvis</label>
              </div>
              <div>
                <label className="label-flat">Admin-note</label>
                <textarea className="input-flat py-1.5 w-full" rows={2} value={adminNote} onChange={(e) => setAdminNote(e.target.value)}/>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-flat">Gebyr</label>
              <select className="input-flat py-1.5 w-full" value={feeCharged} onChange={(e) => setFeeCharged(e.target.value)}>
                <option value="auto">Auto-beregn</option>
                <option value="yes">Opkræv</option>
                <option value="no">Eftergiv</option>
              </select>
            </div>
            <div>
              <label className="label-flat">Beløb (kr)</label>
              <input className="input-flat py-1.5 w-full" type="number" min="0" step="1" value={feeAmount} onChange={(e) => setFeeAmount(e.target.value)} placeholder="auto"/>
            </div>
          </div>
        </div>
        <div className="border-t border-brand p-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-brand-text2 hover:text-brand-dark">Annullér</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-brand-red hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1" data-testid="admin-fr-modal-save">
            {saving ? <Loader2 size={14} className="animate-spin"/> : null} Bekræft
          </button>
        </div>
      </div>
    </div>
  );
}



function ControlTowerView() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/b2b/control-tower");
      setData(data);
    } catch (e) {
      toast.error("Kunne ikke hente control tower");
    } finally { setLoading(false); }
  }, []);
  React.useEffect(() => {
    load();
    const id = setInterval(load, 30000);  // refresh every 30s
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) return <div className="text-center py-10 text-brand-text2"><Loader2 className="inline animate-spin mr-2"/> Indlæser...</div>;
  if (!data) return null;
  const c = data.counters || {};
  const a = data.alerts || {};
  return (
    <div className="space-y-4" data-testid="admin-control-tower">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl text-brand-dark">Operationel Control Tower</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-brand-text2">Auto-refresh hver 30s · sidste opdatering {new Date(data.fetched_at_iso).toLocaleTimeString("da-DK")}</span>
          <button onClick={load} className="text-xs font-semibold text-brand-dark hover:text-brand-red inline-flex items-center gap-1"><RotateCcw size={12}/> Opdater</button>
        </div>
      </div>

      {/* Today counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <CT label="Oprettet i dag" value={c.created_today} color="brand"/>
        <CT label="Klar til pickup" value={c.ready_for_pickup} color="amber"/>
        <CT label="Afhentet" value={c.picked_up} color="blue"/>
        <CT label="På HUB" value={c.at_hub} color="blue"/>
        <CT label="Ude til levering" value={c.out_for_delivery} color="brand"/>
        <CT label="Leveret i dag" value={c.delivered_today} color="green"/>
        <CT label="Mislykket i dag" value={c.failed_today} color="red" testid="ct-failed-today"/>
        <CT label="Returneret i dag" value={c.returned_today} color="gray"/>
        <CT label="Genlevering ventende" value={c.redelivery_requested_open} color="amber"/>
        <CT label="Manual review åbne" value={c.manual_review_open} color="blue"/>
      </div>

      {/* Alerts panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <AlertCard label="Manifests låst (i dag)" value={`${a.locked_manifests || 0} / ${a.all_manifests_today || 0}`} icon={Lock} ok={(a.locked_manifests || 0) > 0}/>
        <AlertCard label="Kunder uden aftale" value={a.customers_missing_agreement} icon={AlertTriangle} ok={a.customers_missing_agreement === 0}/>
        <AlertCard label="Kunder med API-fejl (24t)" value={a.customers_with_api_errors} icon={AlertTriangle} ok={a.customers_with_api_errors === 0}/>
        <AlertCard label="Faktura-grundlag klar" value={a.invoice_basis_ready_count} icon={Receipt} ok={true}/>
        <AlertCard label="Røde/kritiske ruter" value={a.routes_red_count} icon={AlertTriangle} ok={a.routes_red_count === 0}/>
        <AlertCard label="Aktive aftaler" value={data.health?.active_agreements} icon={CheckCircle2} ok/>
        {/* Sprint 2J margin alerts */}
        <AlertCard label="Margin alerts (kritisk)" value={a.margin_alerts_critical || 0} icon={Bell} ok={(a.margin_alerts_critical || 0) === 0} testid="ct-margin-critical"/>
        <AlertCard label="Margin alerts (advarsel)" value={a.margin_alerts_warning || 0} icon={Bell} ok={(a.margin_alerts_warning || 0) === 0} testid="ct-margin-warning"/>
        <AlertCard label="Margin alerts (info)" value={a.margin_alerts_info || 0} icon={Bell} ok testid="ct-margin-info"/>
      </div>

      <div className="bg-white border border-brand p-4">
        <div className="text-xs uppercase tracking-wider text-brand-text2 mb-2">System</div>
        <div className="inline-flex items-center gap-2 text-sm font-semibold">
          <span className={`w-2 h-2 ${data.health?.system_status === "ok" ? "bg-green-500" : "bg-red-500"}`}></span>
          Status: <span className="text-brand-dark">{data.health?.system_status}</span>
          <span className="mx-2 text-brand-text2">·</span>
          <span className="text-brand-text2">{data.health?.active_agreements} aktive aftaler af {data.health?.total_customers} kunder</span>
        </div>
      </div>
    </div>
  );
}

function CT({ label, value, color = "gray", testid }) {
  const c = { gray: "bg-white border-brand", brand: "bg-brand-bg2 border-brand", amber: "bg-amber-50 border-amber-300", blue: "bg-blue-50 border-blue-300", green: "bg-green-50 border-green-300", red: "bg-red-50 border-red-300" };
  return (
    <div className={`border p-3 ${c[color]}`} data-testid={testid}>
      <div className="text-3xl font-bold text-brand-dark">{value ?? 0}</div>
      <div className="text-xs uppercase tracking-wider text-brand-text2 mt-1">{label}</div>
    </div>
  );
}

function AlertCard({ label, value, icon: Icon, ok, testid }) {
  return (
    <div className={`border p-3 flex items-center gap-3 ${ok ? "bg-green-50 border-green-300" : "bg-amber-50 border-amber-300"}`} data-testid={testid}>
      <Icon size={20} className={ok ? "text-green-700" : "text-amber-700"}/>
      <div>
        <div className="text-xl font-bold text-brand-dark">{value ?? 0}</div>
        <div className="text-xs text-brand-text2">{label}</div>
      </div>
    </div>
  );
}

function InvoiceBasisView({ customers }) {
  const [month, setMonth] = React.useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [filterCust, setFilterCust] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [billyModal, setBillyModal] = React.useState(null);  // {customer_id, customer_name}

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (filterCust) params.customer_id = filterCust;
      if (filterStatus) params.status = filterStatus;
      const { data } = await adminApi().get("/admin/b2b/invoice-basis", { params });
      setData(data);
    } catch (e) { toast.error("Kunne ikke hente"); } finally { setLoading(false); }
  }, [month, filterCust, filterStatus]);

  React.useEffect(() => { load(); }, [load]);

  const updateStatus = async (cid, newStatus) => {
    try {
      await adminApi().patch(`/admin/b2b/invoice-basis/${cid}/${month}/status`, { status: newStatus });
      toast.success(`Status opdateret til ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status-opdatering fejlede");
    }
  };

  const download = async (cid, fmt) => {
    try {
      const tk = localStorage.getItem("ema_admin_token");
      const res = await fetch(`${API}/admin/b2b/invoice-basis/${cid}/${month}/${fmt}`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `faktura_${cid.slice(0, 8)}_${month}.${fmt}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download fejlede"); }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2 });
  const s = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="admin-invoice-view">
      <div className="flex flex-wrap gap-3 items-end bg-white border border-brand p-4">
        <div><label className="label-flat">Måned</label><input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input-flat py-1.5" data-testid="admin-invoice-month"/></div>
        <div><label className="label-flat">Kunde</label>
          <select className="input-flat py-1.5" value={filterCust} onChange={(e) => setFilterCust(e.target.value)} data-testid="admin-invoice-customer">
            <option value="">Alle</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div><label className="label-flat">Status</label>
          <select className="input-flat py-1.5" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="admin-invoice-status">
            <option value="">Alle</option>
            <option value="draft">Udkast</option>
            <option value="reviewed">Gennemset</option>
            <option value="ready">Klar</option>
            <option value="exported">Eksporteret</option>
          </select>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white">Opdater</button>
      </div>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <CT label="Total omsætning (ex moms)" value={`${fmt(s.total_revenue_ex_vat_dkk)} kr`} color="green"/>
          <CT label="Total moms" value={`${fmt(s.total_vat_dkk)} kr`} color="brand"/>
          <CT label="Forsendelser" value={s.total_shipments} color="blue"/>
          <CT label="Kunder klar" value={s.customers_ready_count} color="amber"/>
          <CT label="Mangler aftale" value={s.customers_missing_agreement_count} color={s.customers_missing_agreement_count > 0 ? "red" : "gray"}/>
        </div>
      )}

      {loading ? <div className="text-center py-6 text-brand-text2"><Loader2 className="inline animate-spin mr-2"/> Indlæser...</div>
      : !data?.items?.length ? <div className="border border-brand bg-white p-6 text-center text-brand-text2">Ingen faktura-grundlag for valgte filter</div>
      : (
        <div className="border border-brand bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr>
                <th className="px-3 py-2 text-left">Kunde</th>
                <th className="px-3 py-2 text-right">Forsendelser</th>
                <th className="px-3 py-2 text-right">SD/ND</th>
                <th className="px-3 py-2 text-right">Subtotal ex moms</th>
                <th className="px-3 py-2 text-right">Moms</th>
                <th className="px-3 py-2 text-right">Total inkl. moms</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const t = it.totals || {};
                return (
                  <tr key={it.customer_id} className="border-t border-brand" data-testid={`admin-invoice-row-${it.customer_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{it.customer_name}</div>
                      <div className="text-xs text-brand-text2">CVR {it.customer_cvr}</div>
                    </td>
                    <td className="px-3 py-2 text-right">{t.total_shipments}</td>
                    <td className="px-3 py-2 text-right text-xs"><span className="text-brand-red font-semibold">{t.same_day_count || 0}</span> / <span className="font-semibold">{t.next_day_count || 0}</span></td>
                    <td className="px-3 py-2 text-right">{fmt(t.subtotal_ex_vat_dkk)} kr</td>
                    <td className="px-3 py-2 text-right">{fmt(t.vat_dkk)} kr</td>
                    <td className="px-3 py-2 text-right font-semibold text-brand-red">{fmt(t.total_inc_vat_dkk)} kr</td>
                    <td className="px-3 py-2">
                      <select className="input-flat py-1 text-xs" value={it.status} onChange={(e) => updateStatus(it.customer_id, e.target.value)} data-testid={`admin-invoice-status-${it.customer_id}`}>
                        <option value="draft">Udkast</option>
                        <option value="reviewed">Gennemset</option>
                        <option value="ready">Klar</option>
                        <option value="exported">Eksporteret</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1 items-center">
                        <button onClick={() => download(it.customer_id, "pdf")} className="px-2 py-1 text-xs text-brand-red hover:text-brand-dark" data-testid={`admin-invoice-pdf-${it.customer_id}`}>PDF</button>
                        <button onClick={() => download(it.customer_id, "xlsx")} className="px-2 py-1 text-xs text-brand-dark hover:text-brand-red">Excel</button>
                        <button onClick={() => download(it.customer_id, "csv")} className="px-2 py-1 text-xs text-brand-dark hover:text-brand-red">CSV</button>
                        <button
                          onClick={() => setBillyModal({ customer_id: it.customer_id, customer_name: it.customer_name })}
                          className="px-2 py-1 text-xs font-semibold text-white bg-brand-red hover:bg-brand-dark disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-brand-red"
                          data-testid={`admin-invoice-billy-${it.customer_id}`}
                          disabled={!t.total_shipments}
                          title={t.total_shipments ? "Send til Billy som kladde" : "Ingen forsendelser at sende"}
                        >Send til Billy</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {s.customers_missing_agreement?.length > 0 && (
        <div className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
          <b>Kunder med forsendelser men uden aktiv aftale:</b> {s.customers_missing_agreement.join(", ")}
        </div>
      )}

      {billyModal && (
        <BillyPushModal
          customerId={billyModal.customer_id}
          customerName={billyModal.customer_name}
          month={month}
          onClose={() => setBillyModal(null)}
          onSuccess={() => { setBillyModal(null); load(); }}
        />
      )}
    </div>
  );
}

function BillyPushModal({ customerId, customerName, month, onClose, onSuccess }) {
  const [preview, setPreview] = React.useState(null);
  const [audit, setAudit] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [pushing, setPushing] = React.useState(false);
  const [force, setForce] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        adminApi().get(`/admin/b2b/invoice-basis/${customerId}/${month}/billy-preview`),
        adminApi().get(`/admin/b2b/invoice-basis/${customerId}/${month}/audit`),
      ]);
      setPreview(p.data);
      setAudit(a.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente Billy preview");
      onClose();
    } finally { setLoading(false); }
  }, [customerId, month, onClose]);

  React.useEffect(() => { load(); }, [load]);

  const send = async () => {
    setPushing(true);
    try {
      const { data } = await adminApi().post(
        `/admin/b2b/invoice-basis/${customerId}/${month}/send-to-billy`,
        { confirm: true, force },
      );
      if (data.mock) {
        toast.success(`Mock-mode: ville have oprettet Billy-kladde (${data.lines_count} linjer)`);
      } else {
        toast.success(`Billy-kladde oprettet · id ${String(data.billy_invoice_id).slice(0, 12)}…`);
      }
      onSuccess();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Send til Billy fejlede";
      toast.error(msg);
      load();
    } finally { setPushing(false); }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2 });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="billy-modal">
      <div className="bg-white border border-brand max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-brand-dark text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/60">Send til Billy · {month}</div>
            <div className="font-display font-bold text-lg">{customerName}</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="billy-modal-close">×</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-brand-text2"><Loader2 className="inline animate-spin mr-2"/> Henter preview...</div>
        ) : preview ? (
          <div className="p-5 space-y-4">
            {!preview.billy_enabled && (
              <div className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900" data-testid="billy-mock-banner">
                <b>Mock-mode aktiv</b> — Billy API-nøgle ikke konfigureret. Tryk &quot;Send til Billy&quot; for at registrere en mock-kladde + audit-log (ingen reel Billy-kladde oprettes).
              </div>
            )}
            {preview.already_pushed && (
              <div className="border-l-4 border-green-500 bg-green-50 p-3 text-sm text-green-900" data-testid="billy-already-pushed">
                <b>Allerede sendt</b> — Billy id <code>{String(preview.billy_invoice_id).slice(0, 16)}…</code>
                {preview.billy_pushed_at_iso && <> · {new Date(preview.billy_pushed_at_iso).toLocaleString("da-DK")}</>}
                {preview.billy_mock && <> · MOCK</>}
              </div>
            )}
            {preview.billy_error && !preview.already_pushed && (
              <div className="border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-900" data-testid="billy-last-error">
                <b>Sidste forsøg fejlede:</b> {preview.billy_error}
              </div>
            )}

            <div className="bg-brand-bg2 border border-brand p-3 text-xs">
              <div><b>Eksternt ref:</b> <code>{preview.external_ref}</code></div>
              <div><b>Faktura-modtager:</b> {preview.billing_email || <span className="text-brand-red">⚠ Mangler billing_email på kunde</span>}</div>
              <div><b>Betalingsfrist:</b> {preview.payment_terms_days} dage netto</div>
              <div><b>Tilstand i Billy:</b> DRAFT (kladde) — du afsender selv i Billy bagefter</div>
            </div>

            <div>
              <h3 className="font-display font-bold text-base text-brand-dark mb-2">Fakturalinjer (grupperet pr. produkt-type)</h3>
              <table className="w-full text-sm border border-brand" data-testid="billy-lines-table">
                <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
                  <tr>
                    <th className="px-3 py-2 text-left">Beskrivelse</th>
                    <th className="px-3 py-2 text-right">Antal</th>
                    <th className="px-3 py-2 text-right">Stk. pris</th>
                    <th className="px-3 py-2 text-right">Total ex moms</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((ln, idx) => (
                    <tr key={idx} className="border-t border-brand">
                      <td className="px-3 py-2">{ln.description}</td>
                      <td className="px-3 py-2 text-right">{ln.quantity}</td>
                      <td className="px-3 py-2 text-right">{fmt(ln.unit_price_ex_vat)} kr</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmt(ln.total_ex_vat)} kr</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-brand-bg2 text-sm">
                  <tr>
                    <td colSpan="3" className="px-3 py-2 text-right">Subtotal ex moms</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(preview.totals.subtotal_ex_vat_dkk)} kr</td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="px-3 py-2 text-right">Moms ({preview.totals.vat_pct}%)</td>
                    <td className="px-3 py-2 text-right">{fmt(preview.totals.vat_dkk)} kr</td>
                  </tr>
                  <tr className="bg-brand-red text-white">
                    <td colSpan="3" className="px-3 py-2 text-right font-bold">Total inkl. moms</td>
                    <td className="px-3 py-2 text-right font-bold">{fmt(preview.totals.total_inc_vat_dkk)} kr</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {audit.length > 0 && (
              <div>
                <h3 className="font-display font-bold text-base text-brand-dark mb-2">Audit-log (seneste 10)</h3>
                <div className="border border-brand bg-white text-xs max-h-40 overflow-y-auto" data-testid="billy-audit-log">
                  {audit.slice(0, 10).map((a, i) => (
                    <div key={i} className="px-3 py-1.5 border-b border-brand last:border-b-0">
                      <span className={`font-mono text-xs ${a.action.includes("failed") || a.action.includes("blocked") ? "text-red-700" : "text-green-700"}`}>{a.action}</span>
                      <span className="text-brand-text2 mx-2">·</span>
                      <span>{a.actor}</span>
                      <span className="text-brand-text2 mx-2">·</span>
                      <span>{new Date(a.at_iso).toLocaleString("da-DK")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {preview.already_pushed && (
              <label className="flex items-center gap-2 text-sm text-brand-dark">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="billy-force-checkbox"/>
                Tving gen-send (opretter ny kladde i Billy)
              </label>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-brand">
              <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-brand-text2 bg-brand-bg2 hover:bg-white border border-brand" data-testid="billy-cancel">Annullér</button>
              <button
                onClick={send}
                disabled={pushing || preview.lines.length === 0 || (preview.already_pushed && !force)}
                className="px-4 py-2 text-sm font-bold text-white bg-brand-red hover:bg-brand-dark disabled:opacity-50"
                data-testid="billy-confirm-send"
              >
                {pushing ? <><Loader2 className="inline animate-spin mr-1" size={14}/> Sender...</> :
                 preview.already_pushed ? (force ? "Gen-send til Billy" : "Allerede sendt") :
                 preview.billy_enabled ? "Send til Billy (kladde)" : "Send (mock-mode)"}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ReportsView({ customers }) {
  const [shipmentsByDay, setShipmentsByDay] = React.useState([]);
  const [byCustomer, setByCustomer] = React.useState([]);
  const [failure, setFailure] = React.useState(null);
  const [sourceMix, setSourceMix] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, f, m] = await Promise.all([
        adminApi().get("/admin/b2b/reports/shipments-by-day"),
        adminApi().get("/admin/b2b/reports/shipments-by-customer", { params: { limit: 20 } }),
        adminApi().get("/admin/b2b/reports/failure-rate"),
        adminApi().get("/admin/b2b/reports/source-mix"),
      ]);
      setShipmentsByDay(d.data.items || []);
      setByCustomer(c.data.items || []);
      setFailure(f.data);
      setSourceMix(m.data.items || []);
    } catch (e) {
      toast.error("Kunne ikke hente rapporter");
    } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-6 text-brand-text2"><Loader2 className="inline animate-spin mr-2"/> Indlæser...</div>;

  return (
    <div className="space-y-4" data-testid="admin-reports-view">
      <h2 className="font-display font-bold text-xl text-brand-dark">Rapporter (sidste 30 dage)</h2>

      {failure && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <CT label="Total" value={failure.total} color="brand"/>
          <CT label="Leveret" value={failure.delivered} color="green"/>
          <CT label="Mislykket" value={failure.failed} color="red"/>
          <CT label="Returneret" value={failure.returned} color="gray"/>
          <CT label="Levering rate" value={`${failure.delivery_rate_pct}%`} color={failure.delivery_rate_pct > 90 ? "green" : "amber"}/>
        </div>
      )}

      {/* Top customers */}
      <div className="bg-white border border-brand p-4">
        <h3 className="font-display font-bold text-base text-brand-dark mb-3">Top kunder efter volume</h3>
        <table className="w-full text-sm">
          <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
            <tr>
              <th className="px-3 py-2 text-left">Kunde</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">SD</th>
              <th className="px-3 py-2 text-right">ND</th>
              <th className="px-3 py-2 text-right">Leveret</th>
              <th className="px-3 py-2 text-right">Mislykket</th>
            </tr>
          </thead>
          <tbody>
            {byCustomer.slice(0, 10).map((c, idx) => (
              <tr key={c.customer_id || `top-cust-${idx}`} className="border-t border-brand">
                <td className="px-3 py-2">{c.customer_name || "—"}</td>
                <td className="px-3 py-2 text-right font-semibold">{c.total}</td>
                <td className="px-3 py-2 text-right text-brand-red">{c.same_day}</td>
                <td className="px-3 py-2 text-right">{c.next_day}</td>
                <td className="px-3 py-2 text-right text-green-700">{c.delivered}</td>
                <td className="px-3 py-2 text-right text-red-700">{c.failed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Source mix */}
      <div className="bg-white border border-brand p-4">
        <h3 className="font-display font-bold text-base text-brand-dark mb-3">Kilde-mix (hvor kommer forsendelser fra?)</h3>
        <div className="flex flex-wrap gap-3">
          {sourceMix.map((s) => (
            <div key={s.source} className="bg-brand-bg2 px-4 py-3 border border-brand">
              <div className="text-2xl font-bold text-brand-dark">{s.count}</div>
              <div className="text-xs uppercase text-brand-text2">{s.source}</div>
            </div>
          ))}
          {sourceMix.length === 0 && <div className="text-brand-text2 text-sm">Ingen data</div>}
        </div>
      </div>

      {/* Shipments by day */}
      <div className="bg-white border border-brand p-4">
        <h3 className="font-display font-bold text-base text-brand-dark mb-3">Forsendelser per dag</h3>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {shipmentsByDay.slice(-30).reverse().map((d) => (
            <div key={d.day} className="flex items-center gap-2 text-xs">
              <div className="w-24 font-mono">{d.day}</div>
              <div className="flex-1 bg-brand-bg2 h-5 relative">
                <div className="absolute inset-y-0 left-0 bg-brand-red" style={{ width: `${Math.min(100, d.total)}%` }}/>
              </div>
              <div className="w-12 text-right font-semibold">{d.total}</div>
              <div className="w-20 text-right text-green-700">{d.delivered} lev</div>
              <div className="w-20 text-right text-red-700">{d.failed} fejl</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}




// ============== Sprint 2I Part B — Subcontractor Settlement ==============

function SettlementView() {
  const [period, setPeriod] = React.useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [subcontractors, setSubcontractors] = React.useState([]);
  const [filterSc, setFilterSc] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [detailModal, setDetailModal] = React.useState(null);

  const fmt = (n) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2 });

  const loadSCs = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/subcontractors");
      setSubcontractors(data || []);
    } catch { /* ignore */ }
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (filterSc) params.subcontractor_id = filterSc;
      if (filterStatus) params.status = filterStatus;
      const { data } = await adminApi().get("/admin/subcontractor-settlements", { params });
      setData(data);
    } catch { toast.error("Kunne ikke hente afregninger"); } finally { setLoading(false); }
  }, [period, filterSc, filterStatus]);

  React.useEffect(() => { loadSCs(); }, [loadSCs]);
  React.useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const STATUS_LABELS = {
    draft: "Udkast", pending_review: "Til gennemsyn", approved: "Godkendt",
    sent_to_subcontractor: "Sendt til vognmand", invoice_received: "Faktura modtaget",
    matched: "Matchet", ready_for_payment: "Klar til betaling", paid: "Betalt",
    disputed: "Bestridt", cancelled: "Annulleret",
  };

  return (
    <div className="space-y-4" data-testid="admin-settlement-view">
      <div className="flex flex-wrap gap-3 items-end bg-white border border-brand p-4">
        <div><label className="label-flat">Periode</label>
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="input-flat py-1.5" data-testid="settlement-period"/>
        </div>
        <div><label className="label-flat">Vognmand</label>
          <select className="input-flat py-1.5" value={filterSc} onChange={(e) => setFilterSc(e.target.value)} data-testid="settlement-filter-sc">
            <option value="">Alle</option>
            {subcontractors.map((sc) => <option key={sc.id} value={sc.id}>{sc.company_name}</option>)}
          </select>
        </div>
        <div><label className="label-flat">Status</label>
          <select className="input-flat py-1.5" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="settlement-filter-status">
            <option value="">Alle</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white" data-testid="settlement-refresh">Opdater</button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <CT label="Total omkostning (ex moms)" value={`${fmt(s.total_cost_ex_vat_dkk)} kr`} color="amber"/>
          <CT label="Total inkl moms" value={`${fmt(s.total_cost_inc_vat_dkk)} kr`} color="brand"/>
          <CT label="Til gennemsyn" value={s.settlements_pending_count} color={s.settlements_pending_count > 0 ? "amber" : "gray"}/>
          <CT label="Klar til betaling" value={s.settlements_ready_for_payment_count} color="green"/>
          <CT label="Mangler vognmandfaktura" value={s.subcontractor_invoices_missing} color={s.subcontractor_invoices_missing > 0 ? "red" : "gray"}/>
        </div>
      )}

      {loading ? <div className="text-center py-6 text-brand-text2"><Loader2 className="inline animate-spin mr-2"/> Indlæser...</div>
      : !data?.items?.length ? <div className="border border-brand bg-white p-6 text-center text-brand-text2">Ingen afregninger for valgte filter</div>
      : (
        <div className="border border-brand bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr>
                <th className="px-3 py-2 text-left">Vognmand</th>
                <th className="px-3 py-2 text-right">Linjer</th>
                <th className="px-3 py-2 text-right">Godkendt h</th>
                <th className="px-3 py-2 text-right">Sats</th>
                <th className="px-3 py-2 text-right">Subtotal ex moms</th>
                <th className="px-3 py-2 text-right">Total inkl moms</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const t = it.totals || {};
                return (
                  <tr key={it.subcontractor_id} className="border-t border-brand" data-testid={`settlement-row-${it.subcontractor_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{it.subcontractor_name}</div>
                      <div className="text-xs text-brand-text2">CVR {it.subcontractor_cvr || "—"}{it.needs_review_count > 0 && <span className="ml-2 text-amber-700">⚠ {it.needs_review_count} til review</span>}</div>
                    </td>
                    <td className="px-3 py-2 text-right">{t.approved_lines}/{t.total_lines}</td>
                    <td className="px-3 py-2 text-right">{fmt(t.total_approved_hours)} t</td>
                    <td className="px-3 py-2 text-right text-xs">{fmt(it.settings?.hourly_rate_excl_vat_dkk)} kr/t</td>
                    <td className="px-3 py-2 text-right">{fmt(t.approved_total_ex_vat_dkk)} kr</td>
                    <td className="px-3 py-2 text-right font-semibold text-brand-red">{fmt(t.approved_total_inc_vat_dkk)} kr</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs font-semibold ${it.status === "paid" ? "bg-green-100 text-green-800" : it.status === "disputed" ? "bg-red-100 text-red-800" : it.status === "ready_for_payment" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-800"}`}>
                        {STATUS_LABELS[it.status] || it.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => setDetailModal({ sc_id: it.subcontractor_id, sc_name: it.subcontractor_name, period })}
                        className="px-3 py-1 text-xs font-semibold text-white bg-brand-red hover:bg-brand-dark"
                        data-testid={`settlement-detail-${it.subcontractor_id}`}
                      >Åbn detalje</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detailModal && (
        <SettlementDetailModal {...detailModal} onClose={() => setDetailModal(null)} onChange={load}/>
      )}
    </div>
  );
}

function SettlementDetailModal({ sc_id, sc_name, period, onClose, onChange }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [settings, setSettings] = React.useState(null);
  const [editingLine, setEditingLine] = React.useState(null);

  const fmt = (n) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2 });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        adminApi().get(`/admin/subcontractor-settlements/${sc_id}/${period}`),
        adminApi().get(`/admin/subcontractors/${sc_id}/settlement-settings`),
      ]);
      setData(d.data);
      setSettings(s.data.settlement_settings);
    } catch { toast.error("Kunne ikke hente detalje"); onClose(); }
    finally { setLoading(false); }
  }, [sc_id, period, onClose]);

  React.useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    try {
      await adminApi().patch(`/admin/subcontractor-settlements/${sc_id}/${period}/status`, { status });
      toast.success(`Status: ${status}`);
      load(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status fejlede"); }
  };

  const markPaid = async () => {
    try {
      await adminApi().post(`/admin/subcontractor-settlements/${sc_id}/${period}/mark-paid`, {});
      toast.success("Markeret som betalt");
      load(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mark-paid fejlede"); }
  };

  const exportFile = async (ext) => {
    try {
      const tk = localStorage.getItem("ema_admin_token");
      const res = await fetch(`${API}/admin/subcontractor-settlements/${sc_id}/${period}/${ext}`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `vognmand_afregning_${sc_id.slice(0, 8)}_${period}.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download fejlede"); }
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="settlement-modal">
      <div className="bg-white p-10"><Loader2 className="inline animate-spin mr-2"/> Indlæser...</div>
    </div>
  );

  if (!data) return null;
  const t = data.totals;
  const isLocked = ["paid", "ready_for_payment"].includes(data.status);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="settlement-modal">
      <div className="bg-white border border-brand max-w-6xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-brand-dark text-white px-5 py-3 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/60">Vognmand-afregning · {period}</div>
            <div className="font-display font-bold text-lg">{sc_name}</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="settlement-modal-close">×</button>
        </div>

        <div className="p-5 space-y-4">
          {settings && (
            <div className="bg-brand-bg2 border border-brand p-3 text-xs grid grid-cols-2 md:grid-cols-4 gap-2">
              <div><b>Sats:</b> {fmt(settings.hourly_rate_excl_vat_dkk)} kr/t (ex moms)</div>
              <div><b>Moms:</b> {settings.vat_pct}%</div>
              <div><b>Periode-type:</b> {settings.period}</div>
              <div><b>Mileage:</b> {settings.special_mileage_agreement?.enabled ? `${settings.special_mileage_agreement.rate_dkk_per_km} kr/km` : "Inkluderet i sats"}</div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <CT label="Godkendte linjer" value={`${t.approved_lines}/${t.total_lines}`} color="brand"/>
            <CT label="Godkendte timer" value={`${fmt(t.total_approved_hours)} t`} color="blue"/>
            <CT label="Subtotal ex moms" value={`${fmt(t.approved_total_ex_vat_dkk)} kr`} color="amber"/>
            <CT label={`Moms (${t.vat_pct}%)`} value={`${fmt(t.vat_dkk)} kr`} color="gray"/>
            <CT label="Total inkl moms" value={`${fmt(t.approved_total_inc_vat_dkk)} kr`} color="red"/>
          </div>

          {t.needs_review_count > 0 && (
            <div className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
              <b>⚠ {t.needs_review_count} linjer mangler review</b> — faktisk køretid ikke registreret, beregnet fra planlagt tid
            </div>
          )}

          <div className="border border-brand overflow-x-auto" data-testid="settlement-lines-table">
            <table className="w-full text-xs">
              <thead className="bg-brand-bg2 uppercase text-brand-text2">
                <tr>
                  <th className="px-2 py-2 text-left">Rute</th>
                  <th className="px-2 py-2 text-left">Dato</th>
                  <th className="px-2 py-2 text-right">Stop</th>
                  <th className="px-2 py-2 text-right">Plan h</th>
                  <th className="px-2 py-2 text-right">Faktisk h</th>
                  <th className="px-2 py-2 text-right">Godk. h</th>
                  <th className="px-2 py-2 text-right">Sats</th>
                  <th className="px-2 py-2 text-right">Total ex moms</th>
                  <th className="px-2 py-2 text-center">Godk.</th>
                  <th className="px-2 py-2 text-center">Handling</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((ln) => (
                  <tr key={ln.route_id} className={`border-t border-brand ${ln.needs_review ? "bg-amber-50" : ""}`}>
                    <td className="px-2 py-1.5 font-mono">{ln.route_number || ln.route_id.slice(0, 8)}</td>
                    <td className="px-2 py-1.5">{ln.route_date}</td>
                    <td className="px-2 py-1.5 text-right">{ln.stops_count}</td>
                    <td className="px-2 py-1.5 text-right">{fmt(ln.planned_hours)}</td>
                    <td className="px-2 py-1.5 text-right">{ln.actual_hours !== null ? fmt(ln.actual_hours) : <span className="text-brand-red">—</span>}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">{fmt(ln.approved_hours)}</td>
                    <td className="px-2 py-1.5 text-right">{fmt(ln.hourly_rate_used_dkk)}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">{fmt(ln.total_ex_vat_dkk)}</td>
                    <td className="px-2 py-1.5 text-center">{ln.approved ? <span className="text-green-700 font-bold">✓</span> : "—"}</td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => setEditingLine(ln)} disabled={isLocked}
                        className="px-2 py-0.5 text-xs text-brand-red hover:text-brand-dark disabled:opacity-30" data-testid={`settlement-edit-${ln.route_id}`}>Rediger</button>
                    </td>
                  </tr>
                ))}
                {data.lines.length === 0 && (
                  <tr><td colSpan="10" className="text-center py-6 text-brand-text2">Ingen ruter i perioden</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-2 items-center border-t border-brand pt-3">
            <span className="text-xs text-brand-text2 mr-2">Status:</span>
            <span className={`px-3 py-1 text-xs font-semibold ${data.status === "paid" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>{data.status}</span>
            {data.subcontractor_invoice_number && <span className="text-xs text-brand-text2">· Faktura {data.subcontractor_invoice_number}</span>}
            <div className="ml-auto flex flex-wrap gap-1">
              {!isLocked && data.status === "draft" && <button onClick={() => setStatus("pending_review")} className="px-2 py-1 text-xs bg-brand-bg2 border border-brand">Send til review</button>}
              {!isLocked && data.status === "pending_review" && <button onClick={() => setStatus("approved")} className="px-2 py-1 text-xs bg-green-100 text-green-800 border border-green-300">Godkend</button>}
              {data.status === "approved" && <button onClick={() => setStatus("sent_to_subcontractor")} className="px-2 py-1 text-xs bg-brand-bg2 border border-brand">Send til vognmand</button>}
              {data.status === "sent_to_subcontractor" && <InvoiceReceivedBtn sc_id={sc_id} period={period} onDone={() => { load(); onChange(); }}/>}
              {data.status === "invoice_received" && <button onClick={() => setStatus("ready_for_payment")} className="px-2 py-1 text-xs bg-amber-100 border border-amber-300">Klar til betaling</button>}
              {data.status === "ready_for_payment" && <button onClick={markPaid} className="px-2 py-1 text-xs bg-green-600 text-white">Markér betalt</button>}
              {!["paid", "cancelled"].includes(data.status) && (
                <button onClick={async () => {
                  const reason = window.prompt("Begrundelse for tvist:");
                  if (!reason) return;
                  try {
                    await adminApi().post(`/admin/subcontractor-settlements/${sc_id}/${period}/mark-disputed`, { dispute_reason: reason });
                    toast.success("Bestridt registreret"); load(); onChange();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Tvist fejlede"); }
                }} className="px-2 py-1 text-xs bg-red-100 text-red-800 border border-red-300" data-testid="settlement-dispute">Bestrid</button>
              )}
            </div>
          </div>

          <div className="flex gap-2 border-t border-brand pt-3">
            <button onClick={() => exportFile("pdf")} className="px-3 py-1.5 text-xs font-semibold text-white bg-brand-red hover:bg-brand-dark" data-testid="settlement-export-pdf"><FileDown size={12} className="inline mr-1"/> PDF</button>
            <button onClick={() => exportFile("xlsx")} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white">Excel</button>
            <button onClick={() => exportFile("csv")} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white">CSV</button>
          </div>
        </div>

        {editingLine && (
          <LineEditModal sc_id={sc_id} period={period} line={editingLine} onClose={() => setEditingLine(null)} onSaved={() => { setEditingLine(null); load(); onChange(); }}/>
        )}
      </div>
    </div>
  );
}

function InvoiceReceivedBtn({ sc_id, period, onDone }) {
  const submit = async () => {
    const num = window.prompt("Vognmandens fakturanummer:");
    if (!num) return;
    try {
      await adminApi().patch(`/admin/subcontractor-settlements/${sc_id}/${period}/invoice`, { subcontractor_invoice_number: num });
      toast.success("Faktura registreret"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejlede"); }
  };
  return <button onClick={submit} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 border border-blue-300" data-testid="settlement-invoice-received">Faktura modtaget</button>;
}

function LineEditModal({ sc_id, period, line, onClose, onSaved }) {
  const [form, setForm] = React.useState({
    approved_hours_override: line.approved_hours_override ?? "",
    approved_hours_override_reason: line.approved_hours_override_reason || "",
    hourly_rate_override: line.hourly_rate_override ?? "",
    extra_fees: line.extra_fees || 0,
    waiting_time_fee: line.waiting_time_fee || 0,
    parking_bridge_ferry_fees: line.parking_bridge_ferry_fees || 0,
    no_show_fee: line.no_show_fee || 0,
    damage_claim_amount: line.damage_claim_amount || 0,
    manual_adjustment: line.manual_adjustment || 0,
    deductions: line.deductions || 0,
    mileage_agreed: !!line.mileage_agreed,
    mileage_km: line.mileage_km || 0,
    mileage_rate_dkk_per_km: line.mileage_rate_dkk_per_km || 0,
    admin_notes: line.admin_notes || "",
    approved: !!line.approved,
  });
  const [busy, setBusy] = React.useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.approved_hours_override === "") payload.approved_hours_override = null;
      else payload.approved_hours_override = parseFloat(payload.approved_hours_override);
      if (payload.hourly_rate_override === "") payload.hourly_rate_override = null;
      else payload.hourly_rate_override = parseFloat(payload.hourly_rate_override);
      await adminApi().patch(`/admin/subcontractor-settlements/${sc_id}/${period}/lines/${line.route_id}`, payload);
      toast.success("Linje gemt");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Linje fejlede"); }
    finally { setBusy(false); }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="line-edit-modal">
      <div className="bg-white border border-brand max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-brand-dark text-white px-5 py-3 flex items-center justify-between">
          <div className="font-display font-bold">Rediger linje · {line.route_number}</div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-flat">Godk. timer override (h)</label>
              <input type="number" step="0.25" className="input-flat" value={form.approved_hours_override}
                     onChange={(e) => set("approved_hours_override", e.target.value)} data-testid="line-hours-override"/>
              <div className="text-xs text-brand-text2 mt-1">Planlagt {line.planned_hours}t · Faktisk {line.actual_hours ?? "—"}t · Kilde {line.approved_hours_source}</div>
            </div>
            <div>
              <label className="label-flat">Sats override (kr/t)</label>
              <input type="number" step="1" className="input-flat" value={form.hourly_rate_override}
                     onChange={(e) => set("hourly_rate_override", e.target.value)}/>
              <div className="text-xs text-brand-text2 mt-1">Aktuel: {line.hourly_rate_used_dkk} kr/t</div>
            </div>
          </div>
          <div>
            <label className="label-flat">Begrundelse for override</label>
            <input className="input-flat" value={form.approved_hours_override_reason}
                   onChange={(e) => set("approved_hours_override_reason", e.target.value)} placeholder="F.eks. chauffør tog ekstra stop" data-testid="line-override-reason"/>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {["extra_fees","waiting_time_fee","parking_bridge_ferry_fees","no_show_fee","damage_claim_amount","manual_adjustment","deductions"].map((k) => (
              <div key={k}>
                <label className="label-flat text-xs">{k.replace(/_/g, " ")}</label>
                <input type="number" step="0.01" className="input-flat" value={form[k]} onChange={(e) => set(k, parseFloat(e.target.value) || 0)}/>
              </div>
            ))}
          </div>

          <div className="border border-brand bg-brand-bg2 p-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.mileage_agreed} onChange={(e) => set("mileage_agreed", e.target.checked)}/>
              <b>Special mileage aftalt for denne rute</b>
            </label>
            <div className="text-xs text-brand-text2 mt-1">Kun gyldigt hvis vognmandens special_mileage_agreement var godkendt FØR ruten startede.</div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><label className="text-xs">Km kørt</label><input type="number" step="0.1" className="input-flat py-1" value={form.mileage_km} onChange={(e) => set("mileage_km", parseFloat(e.target.value) || 0)}/></div>
              <div><label className="text-xs">Sats kr/km</label><input type="number" step="0.01" className="input-flat py-1" value={form.mileage_rate_dkk_per_km} onChange={(e) => set("mileage_rate_dkk_per_km", parseFloat(e.target.value) || 0)}/></div>
            </div>
          </div>

          <div>
            <label className="label-flat">Admin noter</label>
            <textarea className="input-flat" rows={2} value={form.admin_notes} onChange={(e) => set("admin_notes", e.target.value)}/>
          </div>

          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={form.approved} onChange={(e) => set("approved", e.target.checked)} data-testid="line-approved"/>
            Godkend linje (kun godkendte linjer tæller i total)
          </label>

          <div className="flex justify-end gap-2 border-t border-brand pt-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-brand-text2 bg-brand-bg2 border border-brand">Annullér</button>
            <button onClick={save} disabled={busy} className="px-4 py-2 text-sm font-bold text-white bg-brand-red hover:bg-brand-dark disabled:opacity-50" data-testid="line-save">{busy ? "Gemmer..." : "Gem linje"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============== Finance Overview — Revenue vs Cost Margin ==============

function FinanceOverviewView() {
  const [period, setPeriod] = React.useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const fmt = (n) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2 });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/b2b/finance-overview", { params: { period } });
      setData(data);
    } catch { toast.error("Kunne ikke hente finance overview"); }
    finally { setLoading(false); }
  }, [period]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-finance-view">
      <div className="flex flex-wrap gap-3 items-end bg-white border border-brand p-4">
        <div><label className="label-flat">Periode</label>
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="input-flat py-1.5" data-testid="finance-period"/>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white">Opdater</button>
      </div>

      {loading ? <div className="text-center py-6 text-brand-text2"><Loader2 className="inline animate-spin mr-2"/> Beregner...</div>
      : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CT label="Omsætning (ex moms)" value={`${fmt(data.customer_revenue_ex_vat_dkk)} kr`} color="green"/>
            <CT label="Vognmand-omkostning (ex moms)" value={`${fmt(data.subcontractor_cost_ex_vat_dkk)} kr`} color="amber"/>
            <CT label="Bruttomargin" value={`${fmt(data.gross_margin_dkk)} kr`} color={data.gross_margin_dkk >= 0 ? "brand" : "red"}/>
            <CT label="Margin %" value={data.gross_margin_pct !== null ? `${data.gross_margin_pct}%` : "—"} color={(data.gross_margin_pct || 0) >= 25 ? "green" : "amber"}/>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CT label="Afregninger til godkendelse" value={data.settlements_pending_approval} color={data.settlements_pending_approval > 0 ? "amber" : "gray"}/>
            <CT label="Faktura-grundlag klar" value={data.customer_invoice_basis_ready} color="blue"/>
            <CT label="Vognmandfakturaer mangler" value={data.subcontractor_invoices_missing} color={data.subcontractor_invoices_missing > 0 ? "red" : "gray"}/>
            <CT label="Røde/ulønsomme ruter" value={data.red_unprofitable_routes_count} color={data.red_unprofitable_routes_count > 0 ? "red" : "gray"}/>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-brand p-4">
              <h3 className="font-display font-bold text-base text-brand-dark mb-3">Omsætning pr. kunde</h3>
              <table className="w-full text-sm">
                <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
                  <tr><th className="px-3 py-2 text-left">Kunde</th><th className="px-3 py-2 text-right">Forsendelser</th><th className="px-3 py-2 text-right">Omsætning ex moms</th></tr>
                </thead>
                <tbody>
                  {data.revenue_per_customer.slice(0, 10).map((c, idx) => (
                    <tr key={c.customer_id || `rev-${idx}`} className="border-t border-brand">
                      <td className="px-3 py-2">{c.customer_name}</td>
                      <td className="px-3 py-2 text-right">{c.shipments}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmt(c.revenue_ex_vat_dkk)} kr</td>
                    </tr>
                  ))}
                  {data.revenue_per_customer.length === 0 && <tr><td colSpan="3" className="px-3 py-4 text-center text-brand-text2 text-xs">Ingen omsætning denne periode</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-brand p-4">
              <h3 className="font-display font-bold text-base text-brand-dark mb-3">Omkostning pr. vognmand</h3>
              <table className="w-full text-sm">
                <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
                  <tr><th className="px-3 py-2 text-left">Vognmand</th><th className="px-3 py-2 text-right">Godk. timer</th><th className="px-3 py-2 text-right">Omkostning ex moms</th><th className="px-3 py-2 text-left">Status</th></tr>
                </thead>
                <tbody>
                  {data.cost_per_subcontractor.slice(0, 10).map((c, idx) => (
                    <tr key={c.subcontractor_id || `cost-${idx}`} className="border-t border-brand">
                      <td className="px-3 py-2">{c.subcontractor_name}</td>
                      <td className="px-3 py-2 text-right">{fmt(c.approved_hours)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmt(c.cost_ex_vat_dkk)} kr</td>
                      <td className="px-3 py-2 text-xs">{c.status}</td>
                    </tr>
                  ))}
                  {data.cost_per_subcontractor.length === 0 && <tr><td colSpan="4" className="px-3 py-4 text-center text-brand-text2 text-xs">Ingen vognmand-omkostning</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {data.red_unprofitable_routes.length > 0 && (
            <div className="bg-white border border-red-300 p-4">
              <h3 className="font-display font-bold text-base text-red-800 mb-3">Røde/ulønsomme ruter (top 20)</h3>
              <table className="w-full text-sm">
                <thead className="bg-red-50 text-xs uppercase text-red-800">
                  <tr><th className="px-3 py-2 text-left">RuteNr</th><th className="px-3 py-2 text-left">Dato</th><th className="px-3 py-2 text-right">Omsætning</th><th className="px-3 py-2 text-right">Omkostning</th><th className="px-3 py-2 text-right">Margin</th></tr>
                </thead>
                <tbody>
                  {data.red_unprofitable_routes.map((r) => (
                    <tr key={r.route_id} className="border-t border-red-100">
                      <td className="px-3 py-2 font-mono">{r.route_number || r.route_id.slice(0, 8)}</td>
                      <td className="px-3 py-2">{r.route_date}</td>
                      <td className="px-3 py-2 text-right">{fmt(r.revenue_dkk)} kr</td>
                      <td className="px-3 py-2 text-right">{fmt(r.cost_dkk)} kr</td>
                      <td className="px-3 py-2 text-right font-bold text-red-800">{fmt(r.margin_dkk)} kr</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============== Sprint 2J — Margin Alerts Center ==============

const ALERT_TYPE_LABELS = {
  route_red_or_critical: "Rute: lav margin",
  route_negative_profit: "Rute: negativ profit",
  cost_per_parcel_above_threshold: "Rute: høj omk/pakke",
  sc_settlement_over_budget: "Vognmand: over budget",
  sc_hours_overrun: "Vognmand: timer overrun",
  customer_revenue_lower_than_sc_cost: "Kunde: rev < cost",
  customer_monthly_margin_low: "Kunde: lav månedsmargin",
  customer_failed_deliveries_high: "Kunde: mange fejlede",
  sc_failed_deliveries_high: "Vognmand: mange fejlede",
  invoice_basis_ready_not_sent_billy: "Faktura klar, ikke sendt til Billy",
  sc_settlement_pending_too_long: "Afregning: ventet for længe",
};

const SEVERITY_STYLES = {
  critical: { bg: "bg-red-100", border: "border-red-300", text: "text-red-800", icon: "text-red-600" },
  warning: { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-800", icon: "text-amber-600" },
  info: { bg: "bg-blue-100", border: "border-blue-300", text: "text-blue-800", icon: "text-blue-600" },
};

function AlertCenterView() {
  const [filterSeverity, setFilterSeverity] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("open");
  const [filterType, setFilterType] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [thresholds, setThresholds] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [rescanning, setRescanning] = React.useState(false);
  const [showThresholds, setShowThresholds] = React.useState(false);
  const [actionAlert, setActionAlert] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSeverity) params.severity = filterSeverity;
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.alert_type = filterType;
      const [list, sum, thr] = await Promise.all([
        adminApi().get("/admin/margin-alerts", { params }),
        adminApi().get("/admin/margin-alerts/summary"),
        adminApi().get("/admin/margin-alerts/thresholds"),
      ]);
      setItems(list.data.items || []);
      setSummary(sum.data);
      setThresholds(thr.data);
    } catch { toast.error("Kunne ikke hente alerts"); }
    finally { setLoading(false); }
  }, [filterSeverity, filterStatus, filterType]);

  React.useEffect(() => { load(); }, [load]);

  const rescan = async () => {
    setRescanning(true);
    try {
      const { data } = await adminApi().post("/admin/margin-alerts/rescan", {});
      toast.success(`Scan færdig: ${data.total} alerts genereret/opdateret`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rescan fejlede"); }
    finally { setRescanning(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-alerts-view">
      <div className="flex flex-wrap gap-3 items-end bg-white border border-brand p-4">
        <div><label className="label-flat">Severity</label>
          <select className="input-flat py-1.5" value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} data-testid="alerts-filter-severity">
            <option value="">Alle</option>
            <option value="critical">Kritisk</option>
            <option value="warning">Advarsel</option>
            <option value="info">Info</option>
          </select>
        </div>
        <div><label className="label-flat">Status</label>
          <select className="input-flat py-1.5" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="alerts-filter-status">
            <option value="">Alle</option>
            <option value="open">Åbne</option>
            <option value="acknowledged">Bekræftet</option>
            <option value="resolved">Lukket</option>
            <option value="ignored">Ignoreret</option>
          </select>
        </div>
        <div><label className="label-flat">Type</label>
          <select className="input-flat py-1.5" value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="alerts-filter-type">
            <option value="">Alle typer</option>
            {Object.entries(ALERT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white">Opdater</button>
        <button onClick={rescan} disabled={rescanning} className="px-3 py-1.5 text-xs font-semibold text-white bg-brand-red hover:bg-brand-dark disabled:opacity-50" data-testid="alerts-rescan">
          {rescanning ? <><Loader2 size={12} className="inline animate-spin mr-1"/> Scanner...</> : "Kør ny scan nu"}
        </button>
        <button onClick={() => setShowThresholds(true)} className="px-3 py-1.5 text-xs font-semibold text-brand-dark border border-brand hover:bg-brand-bg2" data-testid="alerts-thresholds-btn">Tærskler</button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CT label="Åbne kritiske" value={summary.open_critical} color={summary.open_critical > 0 ? "red" : "gray"} testid="alerts-summary-critical"/>
          <CT label="Åbne advarsler" value={summary.open_warning} color={summary.open_warning > 0 ? "amber" : "gray"} testid="alerts-summary-warning"/>
          <CT label="Åbne info" value={summary.open_info} color="blue" testid="alerts-summary-info"/>
          <CT label="Bekræftet" value={summary.acknowledged} color="brand"/>
        </div>
      )}

      {loading ? <div className="text-center py-6 text-brand-text2"><Loader2 className="inline animate-spin mr-2"/> Indlæser...</div>
      : items.length === 0 ? <div className="border border-brand bg-white p-6 text-center text-brand-text2">Ingen alerts for valgte filter — alt kører grønt</div>
      : (
        <div className="border border-brand bg-white overflow-x-auto" data-testid="alerts-list">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr>
                <th className="px-3 py-2 text-left">Severity</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Besked</th>
                <th className="px-3 py-2 text-left">Ref</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Oprettet</th>
                <th className="px-3 py-2 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => {
                const s = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info;
                return (
                  <tr key={a.id} className="border-t border-brand" data-testid={`alert-row-${a.id}`}>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs font-bold ${s.bg} ${s.text}`}>{a.severity.toUpperCase()}</span>
                    </td>
                    <td className="px-3 py-2 text-xs">{ALERT_TYPE_LABELS[a.alert_type] || a.alert_type}</td>
                    <td className="px-3 py-2">
                      <div className="text-sm">{a.message}</div>
                      {a.drilldown_path && <a href={a.drilldown_path} className="text-xs text-brand-red hover:underline">Gå til {a.ref_kind}</a>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-mono">{a.ref_kind}</div>
                      {a.period && <div className="text-brand-text2">{a.period}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs ${a.status === "resolved" ? "bg-green-100 text-green-800" : a.status === "acknowledged" ? "bg-blue-100 text-blue-800" : a.status === "ignored" ? "bg-gray-200 text-gray-700" : "bg-amber-100 text-amber-800"}`}>{a.status}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-brand-text2">{new Date(a.created_at_iso).toLocaleString("da-DK")}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        {a.status === "open" && <button onClick={() => setActionAlert({ alert: a, action: "ack" })} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 border border-blue-300" data-testid={`alert-ack-${a.id}`}>Bekræft</button>}
                        {(a.status === "open" || a.status === "acknowledged") && <button onClick={() => setActionAlert({ alert: a, action: "resolve" })} className="px-2 py-0.5 text-xs bg-green-100 text-green-800 border border-green-300" data-testid={`alert-resolve-${a.id}`}>Luk</button>}
                        {(a.status === "open" || a.status === "acknowledged") && <button onClick={() => setActionAlert({ alert: a, action: "ignore" })} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 border border-gray-300">Ignorer</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {actionAlert && (
        <AlertActionModal {...actionAlert} onClose={() => setActionAlert(null)} onDone={() => { setActionAlert(null); load(); }}/>
      )}
      {showThresholds && thresholds && (
        <ThresholdsModal thresholds={thresholds.thresholds} defaults={thresholds.defaults} onClose={() => setShowThresholds(false)} onSaved={() => { setShowThresholds(false); load(); }}/>
      )}
    </div>
  );
}

function AlertActionModal({ alert, action, onClose, onDone }) {
  const [comment, setComment] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const labels = { ack: "Bekræft alert", resolve: "Luk alert (kræver begrundelse)", ignore: "Ignorer alert (kræver begrundelse)" };
  const endpoints = { ack: "acknowledge", resolve: "resolve", ignore: "ignore" };

  const submit = async () => {
    if ((action === "resolve" || action === "ignore") && !comment.trim()) {
      toast.error("Begrundelse er påkrævet"); return;
    }
    setBusy(true);
    try {
      await adminApi().patch(`/admin/margin-alerts/${alert.id}/${endpoints[action]}`, { comment });
      toast.success("Handling registreret");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Handling fejlede"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="alert-action-modal">
      <div className="bg-white border border-brand max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="bg-brand-dark text-white px-5 py-3 flex items-center justify-between">
          <div className="font-display font-bold">{labels[action]}</div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-brand-bg2 border border-brand p-3 text-sm">
            <div className="font-semibold">{alert.message}</div>
            <div className="text-xs text-brand-text2 mt-1">{ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type} · {alert.severity}</div>
          </div>
          <div>
            <label className="label-flat">Kommentar {(action === "resolve" || action === "ignore") && <span className="text-brand-red">*</span>}</label>
            <textarea className="input-flat" rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
                      placeholder={action === "ack" ? "Valgfri note..." : "Begrundelse..."} data-testid="alert-action-comment"/>
          </div>
          <div className="flex justify-end gap-2 border-t border-brand pt-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-brand-text2 bg-brand-bg2 border border-brand">Annullér</button>
            <button onClick={submit} disabled={busy} className="px-4 py-2 text-sm font-bold text-white bg-brand-red hover:bg-brand-dark disabled:opacity-50" data-testid="alert-action-submit">
              {busy ? "Gemmer..." : labels[action]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ThresholdsModal({ thresholds, defaults, onClose, onSaved }) {
  const [form, setForm] = React.useState({ ...thresholds });
  const [busy, setBusy] = React.useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      Object.keys(form).forEach((k) => {
        if (k in defaults) payload[k] = parseFloat(form[k]);
      });
      await adminApi().patch("/admin/margin-alerts/thresholds", payload);
      toast.success("Tærskler gemt");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejlede"); }
    finally { setBusy(false); }
  };

  const fields = [
    ["route_margin_warning_pct", "Rute margin advarsel (%)"],
    ["route_margin_critical_pct", "Rute margin kritisk (%)"],
    ["cost_per_parcel_warning_dkk", "Cost/parcel advarsel (kr)"],
    ["cost_per_parcel_critical_dkk", "Cost/parcel kritisk (kr)"],
    ["hours_overrun_warning_pct", "Timer-overrun advarsel (%)"],
    ["customer_monthly_margin_warning_pct", "Kunde månedsmargin advarsel (%)"],
    ["customer_monthly_margin_critical_pct", "Kunde månedsmargin kritisk (%)"],
    ["customer_failed_deliveries_warning", "Kunde fejlede leveringer (antal)"],
    ["sc_failed_deliveries_warning", "Vognmand fejlede leveringer (antal)"],
    ["sc_settlement_pending_days_warning", "Afregning pending dage advarsel"],
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="thresholds-modal">
      <div className="bg-white border border-brand max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-brand-dark text-white px-5 py-3 flex items-center justify-between">
          <div className="font-display font-bold">Margin alert tærskler</div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-brand-text2">Default-værdier vises som placeholder. Ændringer audit-logges.</div>
          <div className="grid grid-cols-2 gap-3">
            {fields.map(([k, label]) => (
              <div key={k}>
                <label className="label-flat text-xs">{label}</label>
                <input type="number" step="0.1" className="input-flat" value={form[k] ?? ""}
                       placeholder={defaults[k]}
                       onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                       data-testid={`thresh-${k}`}/>
                <div className="text-xs text-brand-text2 mt-0.5">Default: {defaults[k]}</div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 border-t border-brand pt-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-brand-text2 bg-brand-bg2 border border-brand">Annullér</button>
            <button onClick={save} disabled={busy} className="px-4 py-2 text-sm font-bold text-white bg-brand-red hover:bg-brand-dark disabled:opacity-50" data-testid="thresh-save">
              {busy ? "Gemmer..." : "Gem tærskler"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============== Sprint 2K — Shifts & Breaks ==============

function ShiftsView() {
  const [period, setPeriod] = React.useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [missingOnly, setMissingOnly] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [detail, setDetail] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [list, rep] = await Promise.all([
        adminApi().get("/admin/shifts", { params: { period, missing_review: missingOnly } }),
        adminApi().get("/admin/shifts/reports/summary", { params: { period } }),
      ]);
      setData(list.data);
      setReport(rep.data);
    } catch { toast.error("Kunne ikke hente skifter"); }
    finally { setLoading(false); }
  }, [period, missingOnly]);

  React.useEffect(() => { load(); }, [load]);

  const fmt = (n) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 1 });

  return (
    <div className="space-y-4" data-testid="admin-shifts-view">
      <div className="flex flex-wrap gap-3 items-end bg-white border border-brand p-4">
        <div><label className="label-flat">Periode</label>
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="input-flat py-1.5" data-testid="shifts-period"/>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} data-testid="shifts-missing-only"/>
          Kun mangler review
        </label>
        <button onClick={load} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white">Opdater</button>
      </div>

      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CT label="Planlagte timer (i alt)" value={`${fmt(report.totals.planned_hours)} t`} color="brand"/>
          <CT label="Faktiske timer (i alt)" value={`${fmt(report.totals.actual_hours)} t`} color={report.totals.variance_pct > 10 ? "amber" : "blue"}/>
          <CT label="Variance" value={`${report.totals.variance_pct}%`} color={Math.abs(report.totals.variance_pct) > 15 ? "red" : "green"}/>
          <CT label="Pause-compliance" value={`${report.compliance.break_compliance_pct}%`} color={report.compliance.break_compliance_pct >= 80 ? "green" : "amber"}/>
        </div>
      )}

      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CT label="Mangler review" value={report.compliance.missing_review_count} color={report.compliance.missing_review_count > 0 ? "amber" : "gray"}/>
          <CT label="Skift ikke afsluttet" value={report.compliance.missing_shift_end_count} color={report.compliance.missing_shift_end_count > 0 ? "red" : "gray"}/>
          <CT label="Pause krævet" value={report.compliance.break_required_routes} color="brand"/>
          <CT label="Pause overholdt" value={report.compliance.break_compliant_routes} color="green"/>
        </div>
      )}

      {loading ? <div className="text-center py-6 text-brand-text2"><Loader2 className="inline animate-spin mr-2"/> Indlæser...</div>
      : !data?.items?.length ? <div className="border border-brand bg-white p-6 text-center text-brand-text2">Ingen skifter i perioden</div>
      : (
        <div className="border border-brand bg-white overflow-x-auto" data-testid="shifts-list">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr>
                <th className="px-3 py-2 text-left">Rute</th>
                <th className="px-3 py-2 text-right">Plan t</th>
                <th className="px-3 py-2 text-right">Faktisk t</th>
                <th className="px-3 py-2 text-right">Godk. t</th>
                <th className="px-3 py-2 text-right">Pause</th>
                <th className="px-3 py-2 text-left">Compliance</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Handling</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const s = it.summary;
                return (
                  <tr key={it.id} className="border-t border-brand" data-testid={`shift-row-${it.route_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs">{it.route_number || it.route_id.slice(0, 8)}</div>
                      <div className="text-xs text-brand-text2">{it.route_type} · {it.route_status}</div>
                    </td>
                    <td className="px-3 py-2 text-right">{fmt(s.planned_hours)}</td>
                    <td className="px-3 py-2 text-right">{s.actual_hours !== null ? fmt(s.actual_hours) : <span className="text-brand-red">—</span>}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(s.approved_hours)}</td>
                    <td className="px-3 py-2 text-right text-xs">{s.total_break_min} / {s.required_break_min} min</td>
                    <td className="px-3 py-2">
                      {s.required_break_min > 0 && (
                        <span className={`px-2 py-0.5 text-xs ${s.break_compliant ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                          {s.break_compliant ? "Pause OK" : "Pause mangler"}
                        </span>
                      )}
                      {s.overrun_pct !== null && s.overrun_pct > 20 && (
                        <span className="ml-1 px-2 py-0.5 text-xs bg-amber-100 text-amber-800">+{s.overrun_pct}% overrun</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs ${it.status === "approved" ? "bg-green-100 text-green-800" : it.status === "reviewed" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`}>{it.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setDetail(it.route_id)} className="px-3 py-1 text-xs font-semibold text-white bg-brand-red hover:bg-brand-dark" data-testid={`shift-detail-${it.route_id}`}>Åbn</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detail && <ShiftDetailModal routeId={detail} onClose={() => setDetail(null)} onChange={load}/>}
    </div>
  );
}

function ShiftDetailModal({ routeId, onClose, onChange }) {
  const [shift, setShift] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { const { data } = await adminApi().get(`/admin/shifts/${routeId}`); setShift(data); }
    catch { toast.error("Kunne ikke hente skift"); onClose(); }
    finally { setLoading(false); }
  }, [routeId, onClose]);

  React.useEffect(() => { load(); }, [load]);

  const review = async () => {
    const notes = window.prompt("Admin noter (valgfri):") ?? "";
    try { await adminApi().patch(`/admin/shifts/${routeId}/review`, { admin_notes: notes }); toast.success("Reviewed"); load(); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fejlede"); }
  };
  const approve = async () => {
    try { await adminApi().patch(`/admin/shifts/${routeId}/approve`); toast.success("Godkendt"); load(); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fejlede"); }
  };
  const adjust = async () => {
    const hours = window.prompt("Justér godkendte timer (h):");
    if (!hours) return;
    const reason = window.prompt("Begrundelse (påkrævet):");
    if (!reason) return;
    try { await adminApi().patch(`/admin/shifts/${routeId}/adjust`, { approved_hours_override: parseFloat(hours), reason }); toast.success("Justeret"); load(); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fejlede"); }
  };

  if (loading) return <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"><div className="bg-white p-10"><Loader2 className="inline animate-spin mr-2"/> Indlæser...</div></div>;
  if (!shift) return null;
  const s = shift.summary;
  const fmt = (n) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2 });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="shift-modal">
      <div className="bg-white border border-brand max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-brand-dark text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/60">Skift · {shift.route_number || shift.route_id.slice(0, 8)}</div>
            <div className="font-display font-bold text-lg">{shift.route_type} · {shift.route_status}</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CT label="Planlagt" value={`${fmt(s.planned_hours)} t`} color="brand"/>
            <CT label="Faktisk" value={s.actual_hours !== null ? `${fmt(s.actual_hours)} t` : "—"} color={(s.overrun_pct || 0) > 20 ? "amber" : "blue"}/>
            <CT label="Godkendt" value={`${fmt(s.approved_hours)} t`} color="green"/>
            <CT label="Pause" value={`${s.total_break_min} / ${s.required_break_min} min`} color={s.break_compliant ? "green" : "amber"}/>
          </div>
          <div className="bg-brand-bg2 border border-brand p-3 text-xs space-y-1">
            <div><b>Kilde til godkendte timer:</b> {s.approved_hours_source}</div>
            {s.overrun_pct !== null && <div><b>Overrun:</b> {s.overrun_pct}%</div>}
            <div><b>Skift start:</b> {shift.start_at_iso ? new Date(shift.start_at_iso).toLocaleString("da-DK") : "—"}</div>
            <div><b>Skift slut:</b> {shift.end_at_iso ? new Date(shift.end_at_iso).toLocaleString("da-DK") : "—"}</div>
            <div><b>Driver ID:</b> {shift.driver_id || "—"}</div>
            <div><b>Subcontractor ID:</b> {shift.subcontractor_id || "—"}</div>
            {shift.skipped_break_reason && <div className="text-amber-800"><b>Pause sprunget over — chauffør angav:</b> {shift.skipped_break_reason}</div>}
          </div>

          {shift.breaks?.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-1">Pauser ({shift.breaks.length})</h4>
              <div className="border border-brand text-xs">
                {shift.breaks.map((b, i) => (
                  <div key={b.id || i} className="px-3 py-1.5 border-b border-brand last:border-0 flex items-center justify-between">
                    <span>{new Date(b.start_at_iso).toLocaleTimeString("da-DK")} → {b.end_at_iso ? new Date(b.end_at_iso).toLocaleTimeString("da-DK") : "(åben)"}</span>
                    <span className="text-brand-text2">{b.paid ? "Betalt" : "Ubetalt"}{b.included_in_route_time ? " · inkluderet i rute" : ""}{b.comment ? ` · ${b.comment}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {shift.admin_notes && <div className="bg-blue-50 border border-blue-200 p-3 text-sm"><b>Admin noter:</b> {shift.admin_notes}</div>}
          {shift.approved_hours_override_reason && <div className="bg-amber-50 border border-amber-200 p-3 text-sm"><b>Override grund:</b> {shift.approved_hours_override_reason}</div>}

          <div className="flex flex-wrap gap-2 border-t border-brand pt-3">
            <button onClick={adjust} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white" data-testid="shift-adjust">Justér timer</button>
            {(shift.status === "planned" || shift.status === "started" || shift.status === "completed") && (
              <button onClick={review} className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700" data-testid="shift-review">Markér reviewed</button>
            )}
            {shift.status === "reviewed" && (
              <button onClick={approve} className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700" data-testid="shift-approve">Godkend skift</button>
            )}
            <button onClick={onClose} className="ml-auto px-3 py-1.5 text-xs text-brand-text2 bg-brand-bg2 border border-brand">Luk</button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============== Sprint 2L — Route Completion Emails admin view ==============

function RouteEmailsView() {
  const [filterType, setFilterType] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [triggering, setTriggering] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterType) params.email_type = filterType;
      if (filterStatus) params.status = filterStatus;
      const { data } = await adminApi().get("/admin/route-emails", { params });
      setData(data);
    } catch { toast.error("Kunne ikke hente email-log"); }
    finally { setLoading(false); }
  }, [filterType, filterStatus]);

  React.useEffect(() => { load(); }, [load]);

  const trigger = async () => {
    setTriggering(true);
    try {
      const { data } = await adminApi().post("/admin/jobs/route-emails-nightly/trigger", {});
      toast.success(`Job kørt: ${data.subcontractor_emails_sent} vognmand + ${data.driver_emails_sent} chauffør mails`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Job fejlede"); }
    finally { setTriggering(false); }
  };

  const previewEmail = async (route_id, email_type) => {
    try {
      const { data } = await adminApi().post(`/admin/routes/${route_id}/emails/preview/${email_type}`);
      setPreview({ ...data, route_id, email_type });
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview fejlede"); }
  };

  const resend = async (route_id, email_type) => {
    if (!window.confirm(`Gen-send ${email_type}-email for rute ${route_id.slice(0, 8)}?`)) return;
    try {
      const { data } = await adminApi().post(`/admin/routes/${route_id}/emails/send/${email_type}`);
      toast.success(data.mocked ? "Mock-mode: registreret i log" : "Sendt");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send fejlede"); }
  };

  const STATUS_BADGE = {
    sent: "bg-green-100 text-green-800",
    mocked: "bg-blue-100 text-blue-800",
    failed: "bg-red-100 text-red-800",
    error: "bg-red-100 text-red-800",
    missing_recipient: "bg-amber-100 text-amber-800",
    resent_superseded: "bg-gray-100 text-gray-600",
    pending: "bg-amber-100 text-amber-800",
  };

  return (
    <div className="space-y-4" data-testid="admin-route-emails-view">
      <div className="flex flex-wrap gap-3 items-end bg-white border border-brand p-4">
        <div><label className="label-flat">Email-type</label>
          <select className="input-flat py-1.5" value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="emails-filter-type">
            <option value="">Alle</option>
            <option value="driver">Chauffør</option>
            <option value="subcontractor">Vognmand</option>
          </select>
        </div>
        <div><label className="label-flat">Status</label>
          <select className="input-flat py-1.5" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="emails-filter-status">
            <option value="">Alle</option>
            <option value="sent">Sendt</option>
            <option value="mocked">Mock (SMTP off)</option>
            <option value="failed">Fejlede</option>
            <option value="missing_recipient">Mangler modtager</option>
          </select>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white">Opdater</button>
        <button onClick={trigger} disabled={triggering} className="px-3 py-1.5 text-xs font-semibold text-white bg-brand-red hover:bg-brand-dark disabled:opacity-50" data-testid="emails-trigger-nightly">
          {triggering ? <><Loader2 size={12} className="inline animate-spin mr-1"/> Kører...</> : "Kør nightly job nu"}
        </button>
      </div>

      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CT label="Total i log" value={data.summary.total} color="brand"/>
          <CT label="Sendt (rigtigt)" value={data.summary.sent} color="green"/>
          <CT label="Mocked (SMTP off)" value={data.summary.mocked} color="blue"/>
          <CT label="Fejlede / manglende" value={data.summary.failed} color={data.summary.failed > 0 ? "red" : "gray"}/>
        </div>
      )}

      {loading ? <div className="text-center py-6 text-brand-text2"><Loader2 className="inline animate-spin mr-2"/> Indlæser...</div>
      : !data?.items?.length ? <div className="border border-brand bg-white p-6 text-center text-brand-text2">Ingen emails i log</div>
      : (
        <div className="border border-brand bg-white overflow-x-auto" data-testid="emails-list">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr>
                <th className="px-3 py-2 text-left">Tidspunkt</th>
                <th className="px-3 py-2 text-left">Rute</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Modtager</th>
                <th className="px-3 py-2 text-left">Subject</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id} className="border-t border-brand" data-testid={`email-row-${it.id}`}>
                  <td className="px-3 py-2 text-xs text-brand-text2">{new Date(it.created_at_iso).toLocaleString("da-DK")}</td>
                  <td className="px-3 py-2 font-mono text-xs">{it.route_id.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-xs">{it.email_type === "driver" ? "Chauffør" : "Vognmand"}</td>
                  <td className="px-3 py-2 text-xs">{it.recipient || <span className="text-brand-red">mangler</span>}</td>
                  <td className="px-3 py-2 text-xs">{it.subject}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 text-xs ${STATUS_BADGE[it.status] || "bg-gray-100 text-gray-800"}`}>{it.status}</span>
                    {it.error && <div className="text-xs text-red-700 mt-0.5 truncate max-w-xs" title={it.error}>{it.error}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => previewEmail(it.route_id, it.email_type)} className="px-2 py-0.5 text-xs bg-brand-bg2 border border-brand hover:bg-white" data-testid={`email-preview-${it.id}`}>Preview</button>
                      <button onClick={() => resend(it.route_id, it.email_type)} className="px-2 py-0.5 text-xs bg-brand-red text-white hover:bg-brand-dark" data-testid={`email-resend-${it.id}`}>Gen-send</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && <EmailPreviewModal preview={preview} onClose={() => setPreview(null)}/>}
    </div>
  );
}

function EmailPreviewModal({ preview, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="email-preview-modal">
      <div className="bg-white border border-brand max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-brand-dark text-white px-5 py-3 flex items-center justify-between sticky top-0">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/60">Email Preview · {preview.email_type === "driver" ? "Chauffør" : "Vognmand"}</div>
            <div className="font-display font-bold text-base">Til: {preview.to || <span className="text-amber-300">(mangler modtager)</span>}</div>
            <div className="text-sm text-white/80">{preview.subject}</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="p-4">
          <div className="border border-brand bg-white">
            <iframe srcDoc={preview.html} title="Email preview" className="w-full h-[60vh] border-0" sandbox=""/>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// Sprint 2R-4B — B2B Customer Billing via Billy (admin dashboard)
// ============================================================

const WORKFLOW_META = {
  draft:             { label: "Kladde",           color: "bg-gray-100 text-gray-800" },
  ready_for_review:  { label: "Klar til review",  color: "bg-yellow-100 text-yellow-800" },
  approved:          { label: "Godkendt",         color: "bg-blue-100 text-blue-800" },
  sent_to_billy:     { label: "Sender til Billy", color: "bg-indigo-100 text-indigo-800" },
  billy_created:     { label: "Billy oprettet",   color: "bg-blue-100 text-blue-800" },
  sent_to_customer:  { label: "Afsendt",          color: "bg-amber-100 text-amber-800" },
  paid:              { label: "Betalt",           color: "bg-green-100 text-green-800" },
  overdue:           { label: "Forfalden",        color: "bg-red-100 text-red-800" },
  disputed:          { label: "Bestridt",         color: "bg-orange-100 text-orange-800" },
  credited:          { label: "Krediteret",       color: "bg-gray-200 text-gray-800" },
  failed:            { label: "Billy-fejl",       color: "bg-red-200 text-red-900" },
};

function dkk(v) {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK" }).format(v);
}

function BillingDashboardView({ customers, refresh }) {
  const [drafts, setDrafts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [month, setMonth] = React.useState(() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [customerId, setCustomerId] = React.useState("");
  const [selected, setSelected] = React.useState(null);
  const [showCreate, setShowCreate] = React.useState(false);
  const [showProfile, setShowProfile] = React.useState(null);
  const [showCredit, setShowCredit] = React.useState(null);

  const fetchDrafts = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const { data } = await adminApi().get("/admin/billing/drafts", { params });
      setDrafts(data.items || []);
    } catch (e) {
      toast.error("Kunne ikke hente fakturaer");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  React.useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const onCreateDraft = async (cid, m) => {
    try {
      const { data } = await adminApi().post("/admin/billing/drafts/create", { customer_id: cid, month: m });
      toast.success("Kladde oprettet — " + (data.totals?.total_shipments || 0) + " forsendelser");
      setShowCreate(false);
      fetchDrafts();
      setSelected(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl ved oprettelse");
    }
  };

  const onSweepOverdue = async () => {
    try {
      const { data } = await adminApi().post("/admin/billing/sweep-overdue", {});
      toast.success(`${data.marked_overdue} fakturaer markeret som forfaldne`);
      fetchDrafts();
    } catch {
      toast.error("Sweep fejlede");
    }
  };

  const stats = React.useMemo(() => {
    const s = { total: drafts.length, drafts: 0, ready: 0, billy: 0, paid: 0, overdue: 0, disputed: 0, total_dkk: 0 };
    drafts.forEach((d) => {
      if (d.workflow_status === "draft") s.drafts++;
      else if (d.workflow_status === "ready_for_review") s.ready++;
      else if (["billy_created", "sent_to_customer"].includes(d.workflow_status)) s.billy++;
      else if (d.workflow_status === "paid") s.paid++;
      else if (d.workflow_status === "overdue") s.overdue++;
      else if (d.workflow_status === "disputed") s.disputed++;
      const t = d.totals_with_adjustments?.total_inc_vat_dkk ?? d.totals?.total_inc_vat_dkk ?? 0;
      s.total_dkk += t;
    });
    return s;
  }, [drafts]);

  const filteredDrafts = customerId ? drafts.filter((d) => d.customer_id === customerId) : drafts;

  return (
    <div className="space-y-4" data-testid="billing-dashboard-view">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-brand-text2">Status</label>
            <select className="input-flat" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="billing-filter-status">
              <option value="">Alle</option>
              {Object.entries(WORKFLOW_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-brand-text2">Kunde</label>
            <select className="input-flat" value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="billing-filter-customer">
              <option value="">Alle</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <button onClick={fetchDrafts} className="px-3 py-2 border border-brand rounded text-sm flex items-center gap-1" data-testid="billing-refresh">
            <RefreshCw size={14}/> Opdatér
          </button>
        </div>
        <div className="flex gap-2">
          <button onClick={onSweepOverdue} className="px-3 py-2 border border-brand rounded text-sm" data-testid="billing-sweep">
            Kør overdue-sweep
          </button>
          <button onClick={() => setShowCreate(true)} className="px-3 py-2 bg-brand-red text-white rounded text-sm font-semibold flex items-center gap-1" data-testid="billing-create-draft">
            <Plus size={14}/> Opret kladde
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: "Total", value: stats.total },
          { label: "Kladder", value: stats.drafts },
          { label: "Til review", value: stats.ready },
          { label: "Afsendt", value: stats.billy },
          { label: "Betalt", value: stats.paid },
          { label: "Forfaldne", value: stats.overdue },
          { label: "Bestridt", value: stats.disputed },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-brand bg-brand-bg2 p-3" data-testid={`billing-kpi-${s.label.toLowerCase()}`}>
            <div className="text-xs text-brand-text2">{s.label}</div>
            <div className="text-xl font-bold">{s.value}</div>
          </div>
        ))}
        <div className="rounded-lg border border-brand bg-brand-bg2 p-3 col-span-2">
          <div className="text-xs text-brand-text2">Total beløb (inkl. moms)</div>
          <div className="text-xl font-bold">{dkk(stats.total_dkk)}</div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-brand bg-white">
        <table className="w-full text-sm" data-testid="billing-drafts-table">
          <thead className="bg-brand-bg2 text-brand-text2 text-xs uppercase">
            <tr>
              <th className="px-2 py-2 text-left">Kunde</th>
              <th className="px-2 py-2 text-left">CVR</th>
              <th className="px-2 py-2 text-left">Periode</th>
              <th className="px-2 py-2 text-right">Pakker</th>
              <th className="px-2 py-2 text-right">Ex moms</th>
              <th className="px-2 py-2 text-right">Moms</th>
              <th className="px-2 py-2 text-right">I alt</th>
              <th className="px-2 py-2 text-left">Forfalden</th>
              <th className="px-2 py-2 text-left">Status</th>
              <th className="px-2 py-2 text-left">Billy</th>
              <th className="px-2 py-2 text-right">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="px-2 py-6 text-center text-brand-text2">
                <Loader2 className="inline animate-spin mr-2" size={14}/>Indlæser…
              </td></tr>
            )}
            {!loading && filteredDrafts.length === 0 && (
              <tr><td colSpan={11} className="px-2 py-6 text-center text-brand-text2">Ingen fakturaer fundet</td></tr>
            )}
            {filteredDrafts.map((d) => {
              const meta = WORKFLOW_META[d.workflow_status] || { label: d.workflow_status, color: "bg-gray-100" };
              const totals = d.totals_with_adjustments || d.totals || {};
              const isOverdue = d.workflow_status === "overdue";
              return (
                <tr key={d.id} data-testid={`billing-row-${d.id}`} className={`border-t border-brand hover:bg-brand-bg2/40 ${isOverdue ? "bg-red-50" : ""}`}>
                  <td className="px-2 py-2 font-semibold">{d.customer_name || d.customer_id?.slice(0, 8)}</td>
                  <td className="px-2 py-2 font-mono text-xs">{d.customer_cvr || "—"}</td>
                  <td className="px-2 py-2">{d.billing_period || d.month}</td>
                  <td className="px-2 py-2 text-right">{(d.totals || {}).total_shipments ?? 0}</td>
                  <td className="px-2 py-2 text-right">{dkk(totals.subtotal_ex_vat_dkk)}</td>
                  <td className="px-2 py-2 text-right">{dkk(totals.vat_dkk)}</td>
                  <td className="px-2 py-2 text-right font-semibold">{dkk(totals.total_inc_vat_dkk)}</td>
                  <td className="px-2 py-2 text-xs">{d.due_date_iso || "—"}</td>
                  <td className="px-2 py-2"><span className={`inline-block px-2 py-0.5 rounded text-xs ${meta.color}`} data-testid={`billing-status-${d.id}`}>{meta.label}</span></td>
                  <td className="px-2 py-2 text-xs font-mono">{d.billy_invoice_number || (d.billy_mock ? "MOCK" : "—")}</td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => setSelected(d)} data-testid={`billing-view-${d.id}`}
                            className="px-2 py-1 text-xs rounded border border-brand hover:bg-brand-bg2">Vis</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-brand bg-white p-4">
        <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Building2 size={14}/> Kunde billing-profiler</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-brand-text2 text-xs uppercase">
              <tr>
                <th className="px-2 py-2 text-left">Kunde</th>
                <th className="px-2 py-2 text-left">CVR</th>
                <th className="px-2 py-2 text-left">Billing email</th>
                <th className="px-2 py-2 text-left">Frekvens</th>
                <th className="px-2 py-2 text-left">Kredit</th>
                <th className="px-2 py-2 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {customers.slice(0, 50).map((c) => (
                <tr key={c.id} className="border-t border-brand">
                  <td className="px-2 py-2">{c.company_name}</td>
                  <td className="px-2 py-2 font-mono text-xs">{c.cvr || "—"}</td>
                  <td className="px-2 py-2 text-xs">{c.billing_email || c.invoice_email || "—"}</td>
                  <td className="px-2 py-2 text-xs">{c.billing_frequency || "monthly"}</td>
                  <td className="px-2 py-2 text-xs">
                    <span className={`px-2 py-0.5 rounded ${c.credit_status === "blocked" ? "bg-red-100 text-red-800" : c.credit_status === "on_hold" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>
                      {c.credit_status || "active"}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => setShowProfile(c)} data-testid={`billing-edit-profile-${c.id}`}
                            className="px-2 py-1 text-xs rounded border border-brand hover:bg-brand-bg2 mr-1">Profil</button>
                    <button onClick={() => setShowCredit(c)} data-testid={`billing-credit-hold-${c.id}`}
                            className="px-2 py-1 text-xs rounded border border-orange-300 text-orange-700 hover:bg-orange-50">Kredit-hold</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <BillingDetailModal invoice={selected} onClose={() => { setSelected(null); fetchDrafts(); }} />}
      {showCreate && <CreateDraftModal customers={customers} initialMonth={month} onClose={() => setShowCreate(false)} onCreate={onCreateDraft}/>}
      {showProfile && <BillingProfileModal customer={showProfile} onClose={() => { setShowProfile(null); if (refresh) refresh(); }}/>}
      {showCredit && <CreditHoldModal customer={showCredit} onClose={() => { setShowCredit(null); if (refresh) refresh(); }}/>}
    </div>
  );
}

function CreateDraftModal({ customers, initialMonth, onClose, onCreate }) {
  const [cid, setCid] = React.useState(customers[0]?.id || "");
  const [month, setMonth] = React.useState(initialMonth);
  const [submitting, setSubmitting] = React.useState(false);

  const go = async () => {
    if (!cid || !/^\d{4}-\d{2}$/.test(month)) {
      toast.error("Vælg kunde og angiv måned (YYYY-MM)");
      return;
    }
    setSubmitting(true);
    await onCreate(cid, month);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="create-draft-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-bold mb-4">Opret faktura-kladde</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-brand-text2">Kunde</label>
            <select className="input-flat w-full" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="create-draft-customer">
              <option value="">— vælg kunde —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-brand-text2">Måned (YYYY-MM)</label>
            <input type="month" className="input-flat w-full" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="create-draft-month"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded border border-brand text-sm">Annullér</button>
          <button onClick={go} disabled={submitting} data-testid="create-draft-submit"
                  className="px-4 py-2 rounded bg-brand-red text-white text-sm font-semibold disabled:opacity-50">
            {submitting && <Loader2 className="inline animate-spin mr-1" size={14}/>}Opret kladde
          </button>
        </div>
      </div>
    </div>
  );
}

function BillingProfileModal({ customer, onClose }) {
  const [form, setForm] = React.useState({
    invoice_email: customer.billing_email || customer.invoice_email || "",
    billing_frequency: customer.billing_frequency || "monthly",
    payment_terms_days: customer.payment_terms_days || 14,
    credit_limit_dkk: customer.credit_limit_dkk || 0,
    ean_number: customer.ean_number || "",
    billy_contact_id: customer.billy_contact_id || "",
  });
  const [saving, setSaving] = React.useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await adminApi().patch(`/admin/billing/customers/${customer.id}/billing-profile`, form);
      toast.success("Billing-profil opdateret");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="billing-profile-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-bold mb-4">Billing-profil — {customer.company_name}</h3>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-xs text-brand-text2">Faktura-email</label>
            <input type="email" className="input-flat w-full" value={form.invoice_email} onChange={(e) => set("invoice_email", e.target.value)} data-testid="profile-email"/>
          </div>
          <div>
            <label className="text-xs text-brand-text2">Frekvens</label>
            <select className="input-flat w-full" value={form.billing_frequency} onChange={(e) => set("billing_frequency", e.target.value)} data-testid="profile-frequency">
              <option value="weekly">Ugentligt</option>
              <option value="monthly">Månedligt</option>
              <option value="manual">Manuelt</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-brand-text2">Betalingsfrist (dage)</label>
            <input type="number" min={0} max={120} className="input-flat w-full" value={form.payment_terms_days} onChange={(e) => set("payment_terms_days", parseInt(e.target.value || 0))} data-testid="profile-terms"/>
          </div>
          <div>
            <label className="text-xs text-brand-text2">Kreditmaks (DKK)</label>
            <input type="number" min={0} className="input-flat w-full" value={form.credit_limit_dkk} onChange={(e) => set("credit_limit_dkk", parseFloat(e.target.value || 0))} data-testid="profile-credit-limit"/>
          </div>
          <div>
            <label className="text-xs text-brand-text2">EAN-nummer</label>
            <input type="text" maxLength={20} className="input-flat w-full" value={form.ean_number} onChange={(e) => set("ean_number", e.target.value.replace(/\D/g, ""))} data-testid="profile-ean"/>
          </div>
          <div>
            <label className="text-xs text-brand-text2">Billy contact ID</label>
            <input type="text" className="input-flat w-full" value={form.billy_contact_id} onChange={(e) => set("billy_contact_id", e.target.value)} data-testid="profile-billy-id"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded border border-brand text-sm">Annullér</button>
          <button onClick={save} disabled={saving} data-testid="profile-save"
                  className="px-4 py-2 rounded bg-brand-red text-white text-sm font-semibold disabled:opacity-50">
            {saving && <Loader2 className="inline animate-spin mr-1" size={14}/>}Gem
          </button>
        </div>
      </div>
    </div>
  );
}

function CreditHoldModal({ customer, onClose }) {
  const [onHold, setOnHold] = React.useState(customer.credit_status === "on_hold" || customer.credit_status === "blocked");
  const [reason, setReason] = React.useState(customer.credit_hold_reason || "");
  const [saving, setSaving] = React.useState(false);

  const save = async () => {
    if (reason.trim().length < 4) { toast.error("Angiv begrundelse (mindst 4 tegn)"); return; }
    setSaving(true);
    try {
      await adminApi().post(`/admin/billing/customers/${customer.id}/credit-hold`, { on_hold: onHold, reason: reason.trim() });
      toast.success("Kredit-status opdateret");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="credit-hold-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-bold mb-2">Kredit-hold — {customer.company_name}</h3>
        <p className="text-sm text-brand-text2 mb-3">Når på hold kan der ikke oprettes nye fakturaer. Begrundelse logges i audit-log.</p>
        <label className="flex items-center gap-2 mb-3 text-sm">
          <input type="checkbox" checked={onHold} onChange={(e) => setOnHold(e.target.checked)} data-testid="credit-hold-toggle"/>
          Kunde sat på kredit-hold
        </label>
        <textarea rows={3} className="input-flat w-full" value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Begrundelse" data-testid="credit-hold-reason"/>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded border border-brand text-sm">Annullér</button>
          <button onClick={save} disabled={saving} data-testid="credit-hold-save"
                  className="px-4 py-2 rounded bg-brand-red text-white text-sm font-semibold disabled:opacity-50">
            {saving && <Loader2 className="inline animate-spin mr-1" size={14}/>}Gem
          </button>
        </div>
      </div>
    </div>
  );
}

function BillingDetailModal({ invoice, onClose }) {
  const [detail, setDetail] = React.useState(invoice);
  const [loading, setLoading] = React.useState(true);
  const [adjLabel, setAdjLabel] = React.useState("");
  const [adjCount, setAdjCount] = React.useState(1);
  const [adjPrice, setAdjPrice] = React.useState(0);
  const [adjReason, setAdjReason] = React.useState("");
  const [adjVisible, setAdjVisible] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get(`/admin/billing/drafts/${invoice.id}`);
      setDetail(data);
    } catch {
      toast.error("Kunne ikke hente faktura");
    } finally { setLoading(false); }
  }, [invoice.id]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const totals = detail.totals_with_adjustments || detail.totals || {};
  const meta = WORKFLOW_META[detail.workflow_status] || { label: detail.workflow_status, color: "bg-gray-100" };

  const callAction = async (path, body = {}, msg = "OK") => {
    setBusy(true);
    try {
      await adminApi().post(`/admin/billing/drafts/${invoice.id}/${path}`, body);
      toast.success(msg);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl");
    } finally { setBusy(false); }
  };

  const addAdjustment = async () => {
    if (adjLabel.length < 2) { toast.error("Beskrivelse for kort"); return; }
    if (adjReason.length < 4) { toast.error("Begrundelse for kort (min 4)"); return; }
    setBusy(true);
    try {
      await adminApi().post(`/admin/billing/drafts/${invoice.id}/adjustments`, {
        label: adjLabel, count: adjCount, unit_price_dkk: adjPrice, reason: adjReason, customer_visible: adjVisible,
      });
      setAdjLabel(""); setAdjCount(1); setAdjPrice(0); setAdjReason("");
      toast.success("Justering tilføjet");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl");
    } finally { setBusy(false); }
  };

  const removeAdj = async (adjId) => {
    if (!window.confirm("Fjern denne justering?")) return;
    setBusy(true);
    try {
      await adminApi().delete(`/admin/billing/drafts/${invoice.id}/adjustments/${adjId}`, {
        data: { note: "Fjernet via admin UI" },
      });
      toast.success("Fjernet");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl");
    } finally { setBusy(false); }
  };

  const sendToBilly = async () => {
    if (!window.confirm("Send til Billy som DRAFT? Du skal selv færdig-godkende i Billy bagefter.")) return;
    setBusy(true);
    try {
      const { data } = await adminApi().post(`/admin/billing/drafts/${invoice.id}/send-to-billy`, {});
      if (data.ok) {
        toast.success(`Sendt til Billy — ${data.billy_invoice_number || data.billy_invoice_id || ""} ${data.mock ? "(MOCK)" : ""}`);
      } else {
        toast.error("Billy fejl: " + (data.error || "ukendt"));
      }
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl");
    } finally { setBusy(false); }
  };

  const markPaid = async () => {
    const date = window.prompt("Betalingsdato (YYYY-MM-DD)", new Date().toISOString().slice(0, 10));
    if (!date) return;
    callAction("mark-paid", { paid_date_iso: date, note: "Manuelt registreret" }, "Markeret betalt");
  };

  const markDisputed = async () => {
    const reason = window.prompt("Grund til bestridelse (min. 4 tegn)?");
    if (!reason || reason.length < 4) return;
    callAction("mark-disputed", { reason }, "Markeret som bestridt");
  };

  const creditNote = async () => {
    const amount = parseFloat(window.prompt("Kreditbeløb (DKK)") || "0");
    if (!amount || amount <= 0) return;
    const reason = window.prompt("Begrundelse (min. 4 tegn)?");
    if (!reason || reason.length < 4) return;
    callAction("credit-note", { amount_dkk: amount, reason }, "Kreditnota tilføjet");
  };

  const downloadAppendix = async () => {
    try {
      const token = localStorage.getItem("ema_admin_token");
      const res = await fetch(`${API}/admin/billing/drafts/${invoice.id}/appendix.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bilag-${detail.customer_id?.slice(0, 8)}-${detail.month || detail.billing_period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download fejlede"); }
  };

  const canEdit = !["sent_to_billy", "billy_created", "sent_to_customer", "paid"].includes(detail.workflow_status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="billing-detail-modal">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">{detail.customer_name} — {detail.billing_period || detail.month}</h3>
            <p className="text-xs text-brand-text2">CVR {detail.customer_cvr} · Forfalden {detail.due_date_iso || "—"} · {detail.payment_terms_days} dage</p>
          </div>
          <span className={`px-3 py-1 rounded text-sm font-semibold ${meta.color}`} data-testid="detail-status">{meta.label}</span>
        </div>

        {loading && <div className="text-brand-text2 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Indlæser…</div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="rounded border border-brand p-3"><div className="text-xs text-brand-text2">Subtotal ex moms</div><div className="text-lg font-bold">{dkk(totals.subtotal_ex_vat_dkk)}</div></div>
          <div className="rounded border border-brand p-3"><div className="text-xs text-brand-text2">Moms ({totals.vat_pct ?? 25}%)</div><div className="text-lg font-bold">{dkk(totals.vat_dkk)}</div></div>
          <div className="rounded border border-brand p-3 bg-brand-bg2"><div className="text-xs text-brand-text2">Total inkl. moms</div><div className="text-lg font-bold">{dkk(totals.total_inc_vat_dkk)}</div></div>
        </div>

        {detail.billy_error && (
          <div className="rounded bg-red-50 border border-red-200 p-3 mb-3 text-sm text-red-800" data-testid="billy-error-banner">
            <strong>Billy-fejl:</strong> {detail.billy_error}
          </div>
        )}

        {detail.billy_invoice_id && (
          <div className="rounded bg-blue-50 border border-blue-200 p-3 mb-3 text-sm text-blue-800">
            <strong>Billy DRAFT:</strong> {detail.billy_invoice_number || detail.billy_invoice_id} {detail.billy_mock && <span className="ml-2 px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded text-xs">MOCK</span>}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={downloadAppendix} className="px-3 py-1.5 border border-brand rounded text-sm flex items-center gap-1" data-testid="detail-download-appendix">
            <Download size={14}/> Bilag (CSV)
          </button>
          {detail.workflow_status === "draft" && (
            <button onClick={() => callAction("ready-for-review", {}, "Markeret klar")} disabled={busy}
                    className="px-3 py-1.5 bg-yellow-500 text-white rounded text-sm" data-testid="detail-ready">Markér klar til review</button>
          )}
          {(detail.workflow_status === "draft" || detail.workflow_status === "ready_for_review") && (
            <button onClick={() => callAction("approve", {}, "Godkendt")} disabled={busy}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm" data-testid="detail-approve">Godkend</button>
          )}
          {(detail.workflow_status === "approved" || detail.workflow_status === "failed") && (
            <button onClick={sendToBilly} disabled={busy}
                    className="px-3 py-1.5 bg-brand-red text-white rounded text-sm font-semibold flex items-center gap-1" data-testid="detail-send-billy">
              <Send size={14}/> Send til Billy
            </button>
          )}
          {detail.workflow_status === "billy_created" && (
            <button onClick={() => callAction("mark-sent-to-customer", {}, "Sendt til kunde")} disabled={busy}
                    className="px-3 py-1.5 bg-amber-500 text-white rounded text-sm" data-testid="detail-send-customer">Markér sendt til kunde</button>
          )}
          {["billy_created", "sent_to_customer", "overdue", "disputed"].includes(detail.workflow_status) && (
            <>
              <button onClick={markPaid} disabled={busy} className="px-3 py-1.5 bg-green-600 text-white rounded text-sm" data-testid="detail-mark-paid">Markér betalt</button>
              <button onClick={creditNote} disabled={busy} className="px-3 py-1.5 border border-gray-300 rounded text-sm" data-testid="detail-credit-note">Kreditnota</button>
              <button onClick={markDisputed} disabled={busy} className="px-3 py-1.5 border border-orange-300 text-orange-700 rounded text-sm" data-testid="detail-mark-disputed">Markér bestridt</button>
            </>
          )}
        </div>

        {/* Adjustments */}
        <div className="rounded border border-brand bg-brand-bg2/40 p-3 mb-4">
          <div className="text-sm font-semibold mb-2">Justeringer ({(detail.adjustments || []).length})</div>
          {(detail.adjustments || []).map((adj) => (
            <div key={adj.id} className="flex items-center justify-between text-xs border-t border-brand py-1.5" data-testid={`adj-row-${adj.id}`}>
              <div>
                <strong>{adj.label}</strong> ({adj.count} × {dkk(adj.unit_price_dkk)} = {dkk(adj.total_dkk)})
                {!adj.customer_visible && <span className="ml-2 px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded">Intern</span>}
                <div className="text-brand-text2">{adj.reason} · {adj.admin_user}</div>
              </div>
              {canEdit && (
                <button onClick={() => removeAdj(adj.id)} className="text-red-600 hover:text-red-800" data-testid={`adj-remove-${adj.id}`}>
                  <Trash2 size={14}/>
                </button>
              )}
            </div>
          ))}
          {canEdit && (
            <div className="mt-3 pt-3 border-t border-brand">
              <div className="text-xs font-semibold mb-1">Tilføj justering</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input className="input-flat text-sm" placeholder="Beskrivelse" value={adjLabel} onChange={(e) => setAdjLabel(e.target.value)} data-testid="adj-label"/>
                <input className="input-flat text-sm" type="number" placeholder="Antal" value={adjCount} onChange={(e) => setAdjCount(parseInt(e.target.value || 1))} data-testid="adj-count"/>
                <input className="input-flat text-sm" type="number" step="0.01" placeholder="Stykpris DKK" value={adjPrice} onChange={(e) => setAdjPrice(parseFloat(e.target.value || 0))} data-testid="adj-price"/>
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={adjVisible} onChange={(e) => setAdjVisible(e.target.checked)} data-testid="adj-visible"/>Synlig for kunde</label>
              </div>
              <input className="input-flat text-sm w-full mb-2" placeholder="Begrundelse (min 4 tegn)" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} data-testid="adj-reason"/>
              <button onClick={addAdjustment} disabled={busy} className="px-3 py-1.5 bg-brand-red text-white rounded text-sm" data-testid="adj-add">Tilføj</button>
            </div>
          )}
        </div>

        {/* Audit log */}
        <div className="rounded border border-brand p-3 mb-4">
          <div className="text-sm font-semibold mb-2">Audit-log ({(detail.audit_log || []).length})</div>
          <div className="max-h-48 overflow-y-auto text-xs space-y-1">
            {(detail.audit_log || []).map((a, i) => (
              <div key={i} className="flex justify-between border-b border-brand-bg2 pb-1">
                <span><strong>{a.action}</strong> — {a.actor}</span>
                <span className="text-brand-text2">{a.timestamp?.slice(0, 19).replace("T", " ")}</span>
              </div>
            ))}
            {(detail.audit_log || []).length === 0 && <div className="text-brand-text2 italic">Ingen audit-rækker endnu</div>}
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded bg-brand-dark text-white text-sm">Luk</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Sprint 2R-5 Phase A — Launch Pack admin view
// ============================================================

function LaunchPackView({ customers, refresh }) {
  const [sub, setSub] = React.useState("terms");
  return (
    <div className="space-y-4" data-testid="launch-pack-view">
      <div className="flex gap-1 border-b border-brand mb-3 flex-wrap">
        {[
          { k: "terms",      label: "Betingelser",     icon: BookOpen },
          { k: "ratecards",  label: "Rate Cards",      icon: Receipt },
          { k: "gates",      label: "Booking Gates",   icon: ShieldCheck },
          { k: "hubexc",     label: "HUB-undtagelser", icon: AlertTriangle },
          { k: "claims",     label: "Claims",          icon: PackageX },
          { k: "fuldmagt",   label: "Fuldmagter",      icon: Key },
          { k: "readiness",  label: "Go-Live status",  icon: Rocket },
          { k: "kpi",        label: "KPI",             icon: BarChart3 },
          { k: "templates",  label: "Templates",       icon: Mail },
          { k: "insurance",  label: "Forsikring",      icon: ShieldCheck },
          { k: "esg",        label: "ESG",             icon: Activity },
          { k: "sop",        label: "Daily SOP",       icon: ClipboardList },
          { k: "capacity",   label: "Kapacitet",       icon: Truck },
        ].map((t) => (
          <button key={t.k} onClick={() => setSub(t.k)}
                  data-testid={`launch-subtab-${t.k}`}
                  className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 ${
                    sub === t.k ? "border-brand-red text-brand-red" : "border-transparent text-brand-text2 hover:text-brand-dark"
                  }`}>
            <t.icon size={14}/> {t.label}
          </button>
        ))}
      </div>
      {sub === "terms" && <TermsAdminView/>}
      {sub === "ratecards" && <RateCardsAdminView customers={customers}/>}
      {sub === "gates" && <BookingGatesAdminView customers={customers} refresh={refresh}/>}
      {sub === "hubexc" && <HubExceptionsAdminView customers={customers}/>}
      {sub === "claims" && <ClaimsAdminView customers={customers}/>}
      {sub === "fuldmagt" && <FuldmagtAdminView/>}
      {sub === "readiness" && <ReadinessReportView/>}
      {sub === "kpi" && <KpiDashboardView/>}
      {sub === "templates" && <TemplatesAdminView/>}
      {sub === "insurance" && <InsuranceAdminView/>}
      {sub === "esg" && <EsgAdminView customers={customers}/>}
      {sub === "sop" && <SopAdminView/>}
      {sub === "capacity" && <CapacityAdminView/>}
    </div>
  );
}

function TermsAdminView() {
  const [versions, setVersions] = React.useState([]);
  const [showCreate, setShowCreate] = React.useState(false);
  const [showAcceptances, setShowAcceptances] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/2r5/terms/versions");
      setVersions(data.items || []);
    } catch { toast.error("Kunne ikke hente versioner"); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const activate = async (version) => {
    if (!window.confirm(`Aktivér version ${version}? Den deaktiverer alle andre versioner.`)) return;
    try {
      await adminApi().post(`/admin/2r5/terms/versions/${version}/activate`);
      toast.success("Aktiveret");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };

  const forceReaccept = async (version) => {
    if (!window.confirm(`Tving alle kunder til at acceptere version ${version} igen?`)) return;
    try {
      await adminApi().post(`/admin/2r5/terms/versions/${version}/force-reacceptance`);
      toast.success("Tvunget re-acceptering aktiveret");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };

  return (
    <div className="space-y-3" data-testid="terms-admin-view">
      <div className="flex justify-between items-center">
        <div className="text-sm text-brand-text2">{versions.length} versioner</div>
        <button onClick={() => setShowCreate(true)} data-testid="terms-create-btn"
                className="px-3 py-1.5 bg-brand-red text-white rounded text-sm font-semibold flex items-center gap-1">
          <Plus size={14}/> Ny version
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-brand bg-white">
        <table className="w-full text-sm" data-testid="terms-versions-table">
          <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
            <tr>
              <th className="px-3 py-2 text-left">Version</th>
              <th className="px-3 py-2 text-left">Titel</th>
              <th className="px-3 py-2 text-left">Oprettet</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-3 py-6 text-center"><Loader2 className="animate-spin inline" size={14}/></td></tr>}
            {versions.map((v) => (
              <tr key={v.id} className="border-t border-brand" data-testid={`terms-row-${v.version}`}>
                <td className="px-3 py-2 font-mono">{v.version}</td>
                <td className="px-3 py-2">{v.title}</td>
                <td className="px-3 py-2 text-xs">{(v.created_at_iso || "").slice(0, 10)}</td>
                <td className="px-3 py-2">
                  {v.active
                    ? <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">Aktiv</span>
                    : <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">Inaktiv</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {!v.active && (
                    <button onClick={() => activate(v.version)} data-testid={`terms-activate-${v.version}`}
                            className="px-2 py-1 text-xs rounded border border-brand mr-1">Aktivér</button>
                  )}
                  <button onClick={() => setShowAcceptances(v.version)} data-testid={`terms-acceptances-${v.version}`}
                          className="px-2 py-1 text-xs rounded border border-brand mr-1">Acceptér-log</button>
                  <button onClick={() => forceReaccept(v.version)} data-testid={`terms-force-${v.version}`}
                          className="px-2 py-1 text-xs rounded border border-orange-300 text-orange-700">Tving re-accept</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showCreate && <TermsCreateModal onClose={() => { setShowCreate(false); refresh(); }}/>}
      {showAcceptances && <AcceptancesModal version={showAcceptances} onClose={() => setShowAcceptances(null)}/>}
    </div>
  );
}

function TermsCreateModal({ onClose }) {
  const [form, setForm] = React.useState({
    version: `2026-v${new Date().getMonth() + 1}`,
    title: "EMA Kurer B2B Transport-betingelser",
    summary: "",
    body_html: "<p>Indsæt betingelser her (HTML tilladt). Mindst 20 tegn.</p>",
    activate: false, force_reacceptance: false,
  });
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      await adminApi().post("/admin/2r5/terms/versions", form);
      toast.success("Version oprettet");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="terms-create-modal">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold mb-4">Ny terms-version</h3>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-brand-text2">Version</label>
              <input className="input-flat w-full" value={form.version} onChange={(e) => set("version", e.target.value)} data-testid="terms-version-input"/>
            </div>
            <div>
              <label className="text-xs text-brand-text2">Titel</label>
              <input className="input-flat w-full" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="terms-title-input"/>
            </div>
          </div>
          <div>
            <label className="text-xs text-brand-text2">Summary (valgfri)</label>
            <input className="input-flat w-full" value={form.summary} onChange={(e) => set("summary", e.target.value)}/>
          </div>
          <div>
            <label className="text-xs text-brand-text2">Body (HTML)</label>
            <textarea rows={10} className="input-flat w-full font-mono text-xs" value={form.body_html} onChange={(e) => set("body_html", e.target.value)} data-testid="terms-body-input"/>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.activate} onChange={(e) => set("activate", e.target.checked)} data-testid="terms-activate-cb"/>
              Aktivér straks (deaktiverer øvrige)
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.force_reacceptance} onChange={(e) => set("force_reacceptance", e.target.checked)} data-testid="terms-force-cb"/>
              Tving alle kunder til re-acceptering
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded border border-brand text-sm">Annullér</button>
          <button onClick={save} disabled={busy} data-testid="terms-save-btn"
                  className="px-4 py-2 rounded bg-brand-red text-white text-sm font-semibold disabled:opacity-50">
            {busy && <Loader2 className="inline animate-spin mr-1" size={14}/>}Opret
          </button>
        </div>
      </div>
    </div>
  );
}

function AcceptancesModal({ version, onClose }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi().get("/admin/2r5/terms/acceptances");
        setItems((data.items || []).filter((it) => it.terms_version === version));
      } catch (_e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [version]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="acceptances-modal">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold mb-3">Acceptér-log — version {version}</h3>
        {loading ? <Loader2 className="animate-spin" size={14}/> :
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr><th className="px-2 py-1 text-left">Kunde</th><th className="px-2 py-1 text-left">Accepteret af</th><th className="px-2 py-1 text-left">Rolle</th><th className="px-2 py-1 text-left">IP</th><th className="px-2 py-1 text-left">Tidspunkt</th></tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-brand">
                  <td className="px-2 py-1 font-mono text-xs">{a.customer_id?.slice(0, 8)}</td>
                  <td className="px-2 py-1">{a.accepted_by_full_name}</td>
                  <td className="px-2 py-1">{a.role_title}</td>
                  <td className="px-2 py-1 font-mono text-xs">{a.ip_address || "—"}</td>
                  <td className="px-2 py-1 text-xs">{(a.accepted_at_iso || "").slice(0, 19).replace("T", " ")}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="px-2 py-4 text-center text-brand-text2 italic">Ingen acceptér-rækker for denne version</td></tr>}
            </tbody>
          </table>}
        <div className="flex justify-end mt-3">
          <button onClick={onClose} className="px-4 py-2 rounded bg-brand-dark text-white text-sm">Luk</button>
        </div>
      </div>
    </div>
  );
}

function RateCardsAdminView({ customers }) {
  const [items, setItems] = React.useState([]);
  const [filterCid, setFilterCid] = React.useState("");
  const [showCreate, setShowCreate] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = filterCid ? { customer_id: filterCid } : {};
      const { data } = await adminApi().get("/admin/2r5/rate-cards", { params });
      setItems(data.items || []);
    } catch { toast.error("Fejl"); }
    finally { setLoading(false); }
  }, [filterCid]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const activate = async (id) => {
    try { await adminApi().post(`/admin/2r5/rate-cards/${id}/activate`); toast.success("Aktiveret"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };
  const archive = async (id) => {
    if (!window.confirm("Arkivér rate card?")) return;
    try { await adminApi().post(`/admin/2r5/rate-cards/${id}/archive`); toast.success("Arkiveret"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };
  const duplicate = async (id) => {
    try { await adminApi().post(`/admin/2r5/rate-cards/${id}/duplicate`); toast.success("Duplikeret"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };

  return (
    <div className="space-y-3" data-testid="rate-cards-admin-view">
      <div className="flex justify-between items-center">
        <select className="input-flat" value={filterCid} onChange={(e) => setFilterCid(e.target.value)} data-testid="rate-card-filter-customer">
          <option value="">Alle kunder</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
        <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 bg-brand-red text-white rounded text-sm font-semibold flex items-center gap-1" data-testid="rate-card-create-btn">
          <Plus size={14}/> Ny rate card
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-brand bg-white">
        <table className="w-full text-sm" data-testid="rate-cards-table">
          <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
            <tr>
              <th className="px-3 py-2 text-left">Navn</th>
              <th className="px-3 py-2 text-left">Kunde</th>
              <th className="px-3 py-2 text-left">Gyldig fra</th>
              <th className="px-3 py-2 text-right">Pakke-pris</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-6 text-center"><Loader2 className="animate-spin inline" size={14}/></td></tr>}
            {items.map((c) => {
              const cust = customers.find((x) => x.id === c.customer_id);
              return (
                <tr key={c.id} className="border-t border-brand" data-testid={`rate-card-row-${c.id}`}>
                  <td className="px-3 py-2">{c.name}</td>
                  <td className="px-3 py-2 text-xs">{cust?.company_name || c.customer_id?.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-xs">{c.effective_from}</td>
                  <td className="px-3 py-2 text-right">{dkk((c.prices || {}).default_per_parcel_dkk)}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      c.status === "active" ? "bg-green-100 text-green-800" :
                      c.status === "archived" ? "bg-gray-100 text-gray-700" : "bg-yellow-100 text-yellow-800"}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {c.status !== "active" && <button onClick={() => activate(c.id)} data-testid={`rate-card-activate-${c.id}`} className="px-2 py-1 text-xs rounded border border-brand mr-1">Aktivér</button>}
                    <button onClick={() => duplicate(c.id)} data-testid={`rate-card-duplicate-${c.id}`} className="px-2 py-1 text-xs rounded border border-brand mr-1">Duplikér</button>
                    {c.status !== "archived" && <button onClick={() => archive(c.id)} data-testid={`rate-card-archive-${c.id}`} className="px-2 py-1 text-xs rounded border border-orange-300 text-orange-700">Arkivér</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showCreate && <RateCardCreateModal customers={customers} onClose={() => { setShowCreate(false); refresh(); }}/>}
    </div>
  );
}

function RateCardCreateModal({ customers, onClose }) {
  const [form, setForm] = React.useState({
    name: "Standard 2026",
    customer_id: customers[0]?.id || "",
    effective_from: new Date().toISOString().slice(0, 10),
    effective_to: null,
    status: "active",
    prices: { default_per_parcel_dkk: 120, vat_pct: 25.0,
              failed_delivery_fee_dkk: 50, manual_label_fee_dkk: 15,
              unreadable_label_fee_dkk: 15, manual_booking_correction_fee_dkk: 25 },
  });
  const [busy, setBusy] = React.useState(false);
  const setP = (k, v) => setForm({ ...form, prices: { ...form.prices, [k]: parseFloat(v || 0) } });
  const save = async () => {
    setBusy(true);
    try {
      await adminApi().post("/admin/2r5/rate-cards", form);
      toast.success("Oprettet");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="rate-card-create-modal">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold mb-3">Ny rate card</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><label className="text-xs text-brand-text2">Navn</label>
            <input className="input-flat w-full" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rc-name"/></div>
          <div><label className="text-xs text-brand-text2">Kunde</label>
            <select className="input-flat w-full" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="rc-customer">
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select></div>
          <div><label className="text-xs text-brand-text2">Status</label>
            <select className="input-flat w-full" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="rc-status">
              <option value="draft">Kladde</option>
              <option value="active">Aktiv</option>
            </select></div>
          <div><label className="text-xs text-brand-text2">Gyldig fra</label>
            <input type="date" className="input-flat w-full" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="rc-effective-from"/></div>
          <div><label className="text-xs text-brand-text2">Gyldig til (valgfri)</label>
            <input type="date" className="input-flat w-full" value={form.effective_to || ""} onChange={(e) => setForm({ ...form, effective_to: e.target.value || null })}/></div>
          {[
            ["default_per_parcel_dkk", "Standard pr. pakke"],
            ["same_day_dkk", "Same Day"],
            ["next_day_dkk", "Next Day"],
            ["return_dkk", "Retur"],
            ["failed_delivery_fee_dkk", "Mislykket lev. fee"],
            ["manual_label_fee_dkk", "Manuel label-fee"],
            ["unreadable_label_fee_dkk", "Ulæselig label-fee"],
            ["manual_booking_correction_fee_dkk", "Manuel booking-korrektion"],
          ].map(([k, lbl]) => (
            <div key={k}><label className="text-xs text-brand-text2">{lbl} (DKK)</label>
              <input type="number" step="0.01" className="input-flat w-full" value={form.prices[k] ?? ""}
                     onChange={(e) => setP(k, e.target.value)} data-testid={`rc-price-${k}`}/></div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded border border-brand text-sm">Annullér</button>
          <button onClick={save} disabled={busy} data-testid="rc-save"
                  className="px-4 py-2 rounded bg-brand-red text-white text-sm font-semibold disabled:opacity-50">
            {busy && <Loader2 className="inline animate-spin mr-1" size={14}/>}Opret
          </button>
        </div>
      </div>
    </div>
  );
}

function BookingGatesAdminView({ customers, refresh }) {
  const [gates, setGates] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  const fetchGate = React.useCallback(async (cid) => {
    try {
      const { data } = await adminApi().get(`/admin/2r5/customers/${cid}/booking-gates`);
      setGates((g) => ({ ...g, [cid]: data }));
    } catch (_e) { /* ignore */ }
  }, []);

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all(customers.slice(0, 50).map((c) => fetchGate(c.id)));
    } finally { setLoading(false); }
  }, [customers, fetchGate]);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  const setOverride = async (cid) => {
    const reason = window.prompt("Begrundelse for kredit-override (min. 4 tegn)?");
    if (!reason || reason.length < 4) return;
    try {
      await adminApi().post(`/admin/2r5/customers/${cid}/credit-override`,
                              { override_active: true, reason });
      toast.success("Override aktiveret");
      fetchGate(cid);
    } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };

  const setLimit = async (cid) => {
    const limit = parseFloat(window.prompt("Ny kreditmaks (DKK)") || "0");
    if (!limit || limit <= 0) return;
    const reason = window.prompt("Begrundelse (min. 4 tegn)?");
    if (!reason || reason.length < 4) return;
    try {
      await adminApi().patch(`/admin/2r5/customers/${cid}/credit-limit`,
                                { credit_limit_dkk: limit, reason });
      toast.success("Kreditmaks opdateret");
      if (refresh) refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };

  return (
    <div className="space-y-3" data-testid="booking-gates-admin-view">
      <div className="flex justify-between">
        <div className="text-sm text-brand-text2">{customers.length} kunder · viser top 50</div>
        <button onClick={fetchAll} disabled={loading} className="px-3 py-1.5 border border-brand rounded text-sm flex items-center gap-1" data-testid="gates-refresh">
          {loading ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>} Genscan
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-brand bg-white">
        <table className="w-full text-sm" data-testid="gates-table">
          <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
            <tr>
              <th className="px-2 py-2 text-left">Kunde</th>
              <th className="px-2 py-2 text-center">Terms</th>
              <th className="px-2 py-2 text-center">Rate card</th>
              <th className="px-2 py-2 text-center">Kredit</th>
              <th className="px-2 py-2 text-center">Booking gate</th>
              <th className="px-2 py-2 text-left">Blokerende grunde</th>
              <th className="px-2 py-2 text-right">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {customers.slice(0, 50).map((c) => {
              const g = gates[c.id];
              const st = g?.gate_state || {};
              return (
                <tr key={c.id} className="border-t border-brand" data-testid={`gate-row-${c.id}`}>
                  <td className="px-2 py-2">{c.company_name}</td>
                  <td className="px-2 py-2 text-center">{st.terms_accepted ? <CheckCircle2 className="inline text-green-600" size={14}/> : <XCircle className="inline text-red-500" size={14}/>}</td>
                  <td className="px-2 py-2 text-center">{st.rate_card_active ? <CheckCircle2 className="inline text-green-600" size={14}/> : <XCircle className="inline text-red-500" size={14}/>}</td>
                  <td className="px-2 py-2 text-center text-xs">{st.credit_status || "—"}{st.credit_override_active && " (override)"}</td>
                  <td className="px-2 py-2 text-center">
                    {g === undefined ? "..." : g.ok
                      ? <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">PASS</span>
                      : <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded">BLOCK</span>}
                  </td>
                  <td className="px-2 py-2 text-xs">
                    {g?.blocking_reasons?.length ? (
                      <ul className="list-disc list-inside">
                        {g.blocking_reasons.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    ) : "—"}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => setLimit(c.id)} data-testid={`gate-set-limit-${c.id}`} className="px-2 py-1 text-xs rounded border border-brand mr-1">Sæt kreditmaks</button>
                    <button onClick={() => setOverride(c.id)} data-testid={`gate-override-${c.id}`} className="px-2 py-1 text-xs rounded border border-orange-300 text-orange-700">Override</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================
// Sprint 2R-5 Phase B+C+D admin views
// ============================================================

function HubExceptionsAdminView({ customers }) {
  const [items, setItems] = React.useState([]);
  const [filter, setFilter] = React.useState({ queue: "", status: "open" });
  const refresh = React.useCallback(async () => {
    const params = {}; if (filter.queue) params.queue = filter.queue; if (filter.status) params.status = filter.status;
    try { const { data } = await adminApi().get("/admin/2r5b/hub-exceptions", { params }); setItems(data.items || []); }
    catch (_e) { toast.error("Fejl"); }
  }, [filter]);
  React.useEffect(() => { refresh(); }, [refresh]);
  return (
    <div className="space-y-3" data-testid="hub-exceptions-admin-view">
      <div className="flex gap-2 items-end">
        <div><label className="text-xs text-brand-text2">Kø</label>
          <select className="input-flat" value={filter.queue} onChange={(e) => setFilter({ ...filter, queue: e.target.value })} data-testid="hubexc-filter-queue">
            <option value="">Alle</option>
            {["missing_label","unreadable_label","unknown_parcel","damaged_parcel","wrong_customer","wrong_address","overweight_package","package_on_hold","return_to_customer","ready_for_route","awaiting_admin_decision"].map((q) => <option key={q} value={q}>{q}</option>)}
          </select></div>
        <div><label className="text-xs text-brand-text2">Status</label>
          <select className="input-flat" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
            <option value="open">Åbne</option><option value="closed">Lukket</option><option value="on_hold">On hold</option><option value="">Alle</option>
          </select></div>
        <button onClick={refresh} className="px-3 py-1.5 border border-brand rounded text-sm">Genscan</button>
      </div>
      <div className="rounded-lg border border-brand bg-white overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
          <tr><th className="px-2 py-2 text-left">Tracking</th><th className="px-2 py-2 text-left">Kø</th><th className="px-2 py-2 text-left">Reason</th><th className="px-2 py-2 text-right">Gebyr</th><th className="px-2 py-2 text-left">Status</th><th className="px-2 py-2 text-right">Handlinger</th></tr>
        </thead><tbody>
          {items.map((e) => (
            <tr key={e.id} className="border-t border-brand" data-testid={`hubexc-row-${e.id}`}>
              <td className="px-2 py-2 font-mono text-xs">{e.tracking_number || e.parcel_id?.slice(0, 8)}</td>
              <td className="px-2 py-2">{e.queue}</td>
              <td className="px-2 py-2 text-xs">{e.reason}</td>
              <td className="px-2 py-2 text-right">{e.fee_dkk ? dkk(e.fee_dkk) : "—"}</td>
              <td className="px-2 py-2"><span className={`px-2 py-0.5 rounded text-xs ${e.status === "closed" ? "bg-gray-100" : "bg-yellow-100 text-yellow-800"}`}>{e.status}</span></td>
              <td className="px-2 py-2 text-right">
                <button onClick={async () => { const reason = window.prompt("Begrundelse (min 4)?"); if (!reason || reason.length < 4) return; try { await adminApi().post(`/admin/2r5b/hub-exceptions/${e.id}/action`, { action: "close", reason, resolution_note: "Lukket via admin" }); toast.success("Lukket"); refresh(); } catch (er) { toast.error(er.response?.data?.detail || "Fejl"); } }} data-testid={`hubexc-close-${e.id}`} className="px-2 py-1 text-xs rounded border border-brand">Luk</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-brand-text2 italic">Ingen undtagelser</td></tr>}
        </tbody></table>
      </div>
    </div>
  );
}

function ClaimsAdminView({ customers }) {
  const [items, setItems] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const refresh = React.useCallback(async () => {
    try { const { data } = await adminApi().get("/admin/2r5b/claims"); setItems(data.items || []); }
    catch (_e) { toast.error("Fejl"); }
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);
  return (
    <div className="space-y-3" data-testid="claims-admin-view">
      <div className="rounded-lg border border-brand bg-white overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
          <tr><th className="px-2 py-2 text-left">Type</th><th className="px-2 py-2 text-left">Kunde</th><th className="px-2 py-2 text-left">Tracking</th><th className="px-2 py-2 text-right">Beløb</th><th className="px-2 py-2 text-left">Status</th><th className="px-2 py-2 text-left">Indsendt</th><th className="px-2 py-2 text-right">Handlinger</th></tr>
        </thead><tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-t border-brand" data-testid={`claim-row-${c.id}`}>
              <td className="px-2 py-2">{c.claim_type}</td>
              <td className="px-2 py-2 text-xs">{customers.find((x) => x.id === c.customer_id)?.company_name || c.customer_id?.slice(0, 8)}</td>
              <td className="px-2 py-2 font-mono text-xs">{c.tracking_number || "—"}</td>
              <td className="px-2 py-2 text-right">{c.claim_amount_dkk ? dkk(c.claim_amount_dkk) : "—"}</td>
              <td className="px-2 py-2"><span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{c.status}</span></td>
              <td className="px-2 py-2 text-xs">{(c.submitted_at_iso || "").slice(0, 10)}</td>
              <td className="px-2 py-2 text-right">
                <button onClick={() => setSelected(c)} data-testid={`claim-view-${c.id}`} className="px-2 py-1 text-xs rounded border border-brand">Vis</button>
              </td>
            </tr>
          ))}
        </tbody></table>
      </div>
      {selected && <ClaimDetailModal claim={selected} onClose={() => { setSelected(null); refresh(); }}/>}
    </div>
  );
}

function ClaimDetailModal({ claim, onClose }) {
  const [busy, setBusy] = React.useState(false);
  const act = async (action, extra = {}) => {
    setBusy(true);
    try { await adminApi().post(`/admin/2r5b/claims/${claim.id}/action`, { action, ...extra }); toast.success("OK"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="claim-detail-modal">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold mb-2">{claim.claim_type} — {claim.status}</h3>
        <div className="text-sm mb-3"><strong>Kunde:</strong> {claim.customer_id?.slice(0, 8)} · <strong>Tracking:</strong> {claim.tracking_number || "—"} · <strong>Beløb:</strong> {claim.claim_amount_dkk ? dkk(claim.claim_amount_dkk) : "—"}</div>
        <div className="text-sm bg-brand-bg2 rounded p-2 mb-3">Kunde-besked: {claim.customer_message || "—"}</div>
        <div className="flex flex-wrap gap-2">
          <button disabled={busy} onClick={() => act("request_info")} data-testid="claim-request-info" className="px-3 py-1.5 bg-amber-500 text-white rounded text-sm">Bed om info</button>
          <button disabled={busy} onClick={() => act("approve")} data-testid="claim-approve" className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm">Godkend</button>
          <button disabled={busy} onClick={async () => { const r = window.prompt("Begrundelse (min 4)?"); if (!r || r.length < 4) return; act("reject", { reason: r }); }} data-testid="claim-reject" className="px-3 py-1.5 border border-orange-300 text-orange-700 rounded text-sm">Afvis</button>
          <button disabled={busy} onClick={async () => { const a = parseFloat(window.prompt("Kreditbeløb DKK?") || "0"); if (a <= 0) return; act("credit", { credit_amount_dkk: a }); }} data-testid="claim-credit" className="px-3 py-1.5 bg-green-600 text-white rounded text-sm">Kreditér</button>
          <button disabled={busy} onClick={() => act("close")} data-testid="claim-close" className="px-3 py-1.5 bg-brand-dark text-white rounded text-sm">Luk</button>
        </div>
        <div className="flex justify-end mt-4"><button onClick={onClose} className="px-4 py-2 rounded border border-brand text-sm">Annullér</button></div>
      </div>
    </div>
  );
}

function FuldmagtAdminView() {
  const [items, setItems] = React.useState([]);
  const [pid, setPid] = React.useState("");
  const refresh = async () => { try { const { data } = await adminApi().get("/admin/2r5b/fuldmagt"); setItems(data.items || []); } catch (_e) { toast.error("Fejl"); } };
  React.useEffect(() => { refresh(); }, []);
  const sendLink = async () => {
    if (!pid) return toast.error("parcel_id?");
    try { const { data } = await adminApi().post("/admin/2r5b/fuldmagt/send-link", { parcel_id: pid }); toast.success(`Link sendt: ${data.link}`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };
  return (
    <div className="space-y-3" data-testid="fuldmagt-admin-view">
      <div className="rounded-lg border border-brand p-3 bg-brand-bg2/40 flex gap-2 items-end">
        <div className="flex-1"><label className="text-xs text-brand-text2">Parcel ID</label>
          <input className="input-flat w-full" value={pid} onChange={(e) => setPid(e.target.value)} data-testid="fuldmagt-parcel-id"/></div>
        <button onClick={sendLink} className="px-3 py-2 bg-brand-red text-white rounded text-sm font-semibold" data-testid="fuldmagt-send-link">Send fuldmagt-link</button>
      </div>
      <div className="rounded-lg border border-brand bg-white overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
          <tr><th className="px-2 py-2 text-left">Tracking</th><th className="px-2 py-2 text-left">Placement</th><th className="px-2 py-2 text-left">Status</th><th className="px-2 py-2 text-left">Oprettet</th></tr>
        </thead><tbody>
          {items.map((a) => (
            <tr key={a.id} className="border-t border-brand" data-testid={`fuldmagt-row-${a.id}`}>
              <td className="px-2 py-2 font-mono text-xs">{a.tracking_number}</td>
              <td className="px-2 py-2 text-xs">{a.placement || "—"}</td>
              <td className="px-2 py-2"><span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{a.status}</span></td>
              <td className="px-2 py-2 text-xs">{(a.created_at_iso || "").slice(0, 16).replace("T", " ")}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="px-2 py-6 text-center text-brand-text2 italic">Ingen fuldmagter</td></tr>}
        </tbody></table>
      </div>
    </div>
  );
}

function ReadinessReportView() {
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const refresh = async () => {
    setLoading(true);
    try { const { data } = await adminApi().get("/admin/2r5c/readiness-report"); setReport(data); }
    catch (_e) { toast.error("Fejl"); }
    finally { setLoading(false); }
  };
  React.useEffect(() => { refresh(); }, []);
  const enableMigration = async () => {
    if (!window.confirm("Aktivér booking_gate_enforced=True på ALLE live kunder?")) return;
    try { const { data } = await adminApi().post("/admin/2r5c/booking-gate-migration/enable-all"); toast.success(`Migreret: ${data.customers_migrated}`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };
  if (!report) return loading ? <Loader2 className="animate-spin" size={16}/> : <button onClick={refresh}>Generér</button>;
  const colorOf = (s) => s === "pass" ? "bg-green-100 text-green-800" : s === "warn" ? "bg-amber-100 text-amber-800" : s === "blocker" ? "bg-red-100 text-red-800" : s === "fail" ? "bg-orange-100 text-orange-800" : "bg-gray-100 text-gray-600";
  return (
    <div className="space-y-3" data-testid="readiness-view">
      <div className={`rounded-lg p-4 border ${report.status === "go" ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold">{report.launch_readiness_pct}% klar til launch</div>
            <div className="text-sm text-brand-text2">{report.recommendation}</div>
          </div>
          <div className="text-right">
            <span className={`px-4 py-2 rounded text-lg font-bold ${report.status === "go" ? "bg-green-600 text-white" : "bg-red-600 text-white"}`} data-testid="readiness-status-badge">{report.status === "go" ? "GO" : "NO-GO"}</span>
          </div>
        </div>
        <div className="mt-3 text-xs text-brand-text2 grid grid-cols-2 md:grid-cols-5 gap-2">
          <span>Pass: {report.summary.pass}</span><span>Warn: {report.summary.warn}</span><span>Fail: {report.summary.fail}</span><span>Blockers: {report.summary.blocker}</span><span>N/A: {report.summary.not_applicable}</span>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={refresh} className="px-3 py-1.5 border border-brand rounded text-sm" data-testid="readiness-refresh">Opdatér</button>
        <button onClick={enableMigration} className="px-3 py-1.5 bg-brand-red text-white rounded text-sm font-semibold" data-testid="readiness-migrate">Migrér booking-gate på alle live kunder</button>
      </div>
      <div className="rounded-lg border border-brand bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="readiness-table">
          <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2"><tr><th className="px-2 py-2 text-left">Dimension</th><th className="px-2 py-2 text-left">Status</th><th className="px-2 py-2 text-left">Detalje</th></tr></thead>
          <tbody>
            {report.dimensions.map((d) => (
              <tr key={d.id} className="border-t border-brand" data-testid={`readiness-dim-${d.id}`}>
                <td className="px-2 py-2 font-semibold">{d.label}</td>
                <td className="px-2 py-2"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${colorOf(d.status)}`}>{d.status.toUpperCase()}</span></td>
                <td className="px-2 py-2 text-xs text-brand-text2">{d.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiDashboardView() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => { (async () => { try { const r = await adminApi().get("/admin/2r5d/kpi/dashboard"); setData(r.data); } catch (_e) { toast.error("Fejl"); } })(); }, []);
  if (!data) return <Loader2 className="animate-spin" size={16}/>;
  const colorOf = (s) => s === "green" ? "bg-green-100 text-green-800" : s === "amber" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";
  return (
    <div className="space-y-3" data-testid="kpi-view">
      <p className="text-xs text-brand-text2">{data.note}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {Object.entries(data.kpis).map(([k, v]) => (
          <div key={k} className="rounded-lg border border-brand bg-white p-3" data-testid={`kpi-${k}`}>
            <div className="text-xs text-brand-text2">{k.replace(/_/g, " ")}</div>
            <div className="text-xl font-bold flex items-center justify-between">
              <span>{typeof v === "number" ? v.toLocaleString("da-DK") : v}</span>
              {data.statuses[k] && <span className={`px-1.5 py-0.5 rounded text-[10px] ${colorOf(data.statuses[k])}`}>●</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplatesAdminView() {
  const [items, setItems] = React.useState([]);
  const [availableKeys, setAvailableKeys] = React.useState([]);
  const [form, setForm] = React.useState({ key: "delivered", channel: "email", subject: "", body: "", locale: "da" });
  const [busy, setBusy] = React.useState(false);
  const refresh = async () => { try { const { data } = await adminApi().get("/admin/2r5d/templates"); setItems(data.items || []); setAvailableKeys(data.available_keys || []); } catch (_e) { toast.error("Fejl"); } };
  React.useEffect(() => { refresh(); }, []);
  const seedDefaults = async () => { try { const { data } = await adminApi().post("/admin/2r5d/templates/seed-defaults"); toast.success(`Seedet: ${data.seeded}`); refresh(); } catch (_e) { toast.error("Fejl"); } };
  const save = async () => { setBusy(true); try { await adminApi().post("/admin/2r5d/templates", form); toast.success("Gemt"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); } finally { setBusy(false); } };
  const preview = async () => { try { const { data } = await adminApi().post("/admin/2r5d/templates/preview", { key: form.key, channel: form.channel, variables: { tracking_number: "E-TEST-12345", delivery_time: "12:34", customer_name: "Test Kunde", placement: "ved bagdør" } }); window.alert(`Subject: ${data.subject}\n\nBody:\n${data.body}`); } catch (_e) { toast.error("Fejl"); } };
  return (
    <div className="space-y-3" data-testid="templates-view">
      <div className="flex justify-between"><div className="text-sm text-brand-text2">{items.length} templates · {availableKeys.length} keys tilgængelige</div><button onClick={seedDefaults} className="px-3 py-1.5 border border-brand rounded text-sm" data-testid="templates-seed">Seed defaults</button></div>
      <div className="rounded-lg border border-brand p-4 bg-brand-bg2/40 grid grid-cols-2 gap-3">
        <div><label className="text-xs text-brand-text2">Key</label><select className="input-flat w-full" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} data-testid="tmpl-key">{availableKeys.map((k) => <option key={k} value={k}>{k}</option>)}</select></div>
        <div><label className="text-xs text-brand-text2">Channel</label><select className="input-flat w-full" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="tmpl-channel"><option value="email">Email</option><option value="sms">SMS</option><option value="portal">Portal</option></select></div>
        <div className="col-span-2"><label className="text-xs text-brand-text2">Subject (kun email)</label><input className="input-flat w-full" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="tmpl-subject"/></div>
        <div className="col-span-2"><label className="text-xs text-brand-text2">Body (HTML eller SMS-tekst, brug &#123;&#123;variable&#125;&#125;)</label><textarea rows={6} className="input-flat w-full font-mono text-xs" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="tmpl-body"/></div>
        <div className="col-span-2 flex gap-2"><button onClick={preview} className="px-3 py-1.5 border border-brand rounded text-sm" data-testid="tmpl-preview">Preview</button><button onClick={save} disabled={busy} className="px-3 py-1.5 bg-brand-red text-white rounded text-sm font-semibold" data-testid="tmpl-save">Gem</button></div>
      </div>
      <div className="rounded-lg border border-brand bg-white overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-brand-bg2 text-xs uppercase text-brand-text2"><tr><th className="px-2 py-2 text-left">Key</th><th className="px-2 py-2 text-left">Channel</th><th className="px-2 py-2 text-left">Subject</th></tr></thead>
          <tbody>{items.map((t) => <tr key={t.id} className="border-t border-brand"><td className="px-2 py-2 font-mono text-xs">{t.key}</td><td className="px-2 py-2">{t.channel}</td><td className="px-2 py-2 text-xs">{t.subject || "—"}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

function InsuranceAdminView() {
  const [items, setItems] = React.useState([]);
  const [warning, setWarning] = React.useState({ critical: false, warnings: [] });
  const [form, setForm] = React.useState({ kind: "transport", name: "EMA Transport-forsikring", provider: "", effective_from: new Date().toISOString().slice(0, 10), expiry_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10) });
  const refresh = async () => { try { const { data } = await adminApi().get("/admin/2r5d/insurance"); setItems(data.items || []); const w = await adminApi().get("/admin/2r5d/insurance/launch-warning"); setWarning(w.data); } catch (_e) { toast.error("Fejl"); } };
  React.useEffect(() => { refresh(); }, []);
  const save = async () => { try { await adminApi().post("/admin/2r5d/insurance", form); toast.success("Oprettet"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); } };
  return (
    <div className="space-y-3" data-testid="insurance-view">
      {warning.critical && <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="insurance-launch-warning">⚠ Kritisk: {warning.warnings.length} forsikring(er) udløbet eller udløber snart.</div>}
      <div className="rounded-lg border border-brand bg-brand-bg2/40 p-4 grid grid-cols-3 gap-2">
        {[["kind","Type"],["name","Navn"],["provider","Leverandør"],["effective_from","Gyldig fra","date"],["expiry_date","Udløber","date"]].map(([k, lbl, type]) => (
          <div key={k}><label className="text-xs text-brand-text2">{lbl}</label><input type={type || "text"} className="input-flat w-full" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`insurance-${k}`}/></div>
        ))}
        <div className="col-span-3 flex justify-end"><button onClick={save} className="px-3 py-1.5 bg-brand-red text-white rounded text-sm font-semibold" data-testid="insurance-save">Tilføj</button></div>
      </div>
      <div className="rounded-lg border border-brand bg-white overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-brand-bg2 text-xs uppercase text-brand-text2"><tr><th className="px-2 py-2 text-left">Type</th><th className="px-2 py-2 text-left">Navn</th><th className="px-2 py-2 text-left">Leverandør</th><th className="px-2 py-2 text-left">Udløber</th><th className="px-2 py-2 text-left">Status</th></tr></thead>
          <tbody>{items.map((i) => <tr key={i.id} className="border-t border-brand"><td className="px-2 py-2">{i.kind}</td><td className="px-2 py-2">{i.name}</td><td className="px-2 py-2 text-xs">{i.provider || "—"}</td><td className="px-2 py-2 text-xs">{i.expiry_date}</td><td className="px-2 py-2"><span className={`px-2 py-0.5 rounded text-xs ${i.status === "expired" ? "bg-red-100 text-red-800" : i.status === "expiring_soon" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>{i.status}</span></td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

function EsgAdminView({ customers }) {
  const [report, setReport] = React.useState(null);
  const [cid, setCid] = React.useState(customers[0]?.id || "");
  const [period, setPeriod] = React.useState(() => new Date().toISOString().slice(0, 7));
  const fetch = async () => { if (!cid) return; try { const { data } = await adminApi().get("/admin/2r5d/esg/customer-report", { params: { customer_id: cid, period } }); setReport(data); } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); } };
  return (
    <div className="space-y-3" data-testid="esg-view">
      <div className="flex gap-2 items-end">
        <div><label className="text-xs text-brand-text2">Kunde</label><select className="input-flat" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="esg-customer">{customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}</select></div>
        <div><label className="text-xs text-brand-text2">Periode</label><input type="month" className="input-flat" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="esg-period"/></div>
        <button onClick={fetch} className="px-3 py-1.5 bg-brand-red text-white rounded text-sm font-semibold" data-testid="esg-generate">Generér</button>
      </div>
      {report && (
        <div className="rounded-lg border border-brand bg-white p-4 space-y-2">
          <div className="text-xl font-bold">{report.estimated_total_co2e_kg} kg CO2e <span className="text-xs font-normal">(estimeret)</span></div>
          <div className="text-sm">Antal pakker: <strong>{report.total_parcels}</strong> · Leveret: <strong>{report.delivered_parcels}</strong> · Mislykkede: <strong>{report.failed_deliveries}</strong> · Returer: <strong>{report.returns}</strong></div>
          <div className="text-sm">CO2e pr. pakke: <strong>{report.estimated_co2e_per_parcel_kg} kg</strong></div>
          <p className="text-xs text-brand-text2 italic">{report.disclaimer}</p>
        </div>
      )}
    </div>
  );
}

function SopAdminView() {
  const [run, setRun] = React.useState(null);
  React.useEffect(() => { (async () => { try { const { data } = await adminApi().get("/admin/2r5d/sop/today"); setRun(data); } catch (_e) { toast.error("Fejl"); } })(); }, []);
  const check = async (item_id, completed) => { try { await adminApi().post("/admin/2r5d/sop/today/check", { item_id, completed }); const { data } = await adminApi().get("/admin/2r5d/sop/today"); setRun(data); } catch (_e) { toast.error("Fejl"); } };
  if (!run) return <Loader2 className="animate-spin" size={16}/>;
  const phases = ["before", "during", "after"];
  return (
    <div className="space-y-3" data-testid="sop-view">
      <div className="text-sm text-brand-text2">Daily SOP — {run.date}</div>
      {phases.map((ph) => (
        <div key={ph} className="rounded-lg border border-brand bg-white">
          <div className="px-3 py-2 bg-brand-bg2 text-sm font-semibold capitalize">{ph === "before" ? "Før drift" : ph === "during" ? "Under drift" : "Efter drift"}</div>
          <ul className="divide-y divide-brand">
            {(run.items || []).filter((it) => it.phase === ph).map((it) => (
              <li key={it.item_id} className="px-3 py-2 flex items-center gap-2" data-testid={`sop-item-${it.item_id}`}>
                <input type="checkbox" checked={!!it.completed} onChange={(e) => check(it.item_id, e.target.checked)}/>
                <span className={`flex-1 text-sm ${it.completed ? "line-through text-brand-text2" : ""}`}>{it.label}</span>
                {it.completed_by && <span className="text-xs text-brand-text2">{it.completed_by.split("@")[0]}</span>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function CapacityAdminView() {
  const [view, setView] = React.useState(null);
  const [rules, setRules] = React.useState([]);
  const [newRule, setNewRule] = React.useState({ rule_type: "route_max", max_count: 80 });
  const refresh = async () => { try { const v = await adminApi().get("/admin/2r5d/capacity/daily-view"); setView(v.data); const r = await adminApi().get("/admin/2r5d/capacity/rules"); setRules(r.data.items || []); } catch (_e) { toast.error("Fejl"); } };
  React.useEffect(() => { refresh(); }, []);
  const save = async () => { try { await adminApi().post("/admin/2r5d/capacity/rules", newRule); toast.success("Regel gemt"); refresh(); } catch (_e) { toast.error("Fejl"); } };
  return (
    <div className="space-y-3" data-testid="capacity-view">
      {view && (
        <div className={`rounded-lg border p-4 ${view.risk === "red" ? "border-red-300 bg-red-50" : view.risk === "amber" ? "border-amber-300 bg-amber-50" : "border-green-300 bg-green-50"}`}>
          <div className="text-2xl font-bold">{view.booked} / {view.estimated_capacity} pakker</div>
          <div className="text-sm">{view.drivers} chauffører × {view.per_route_max} pr. rute · Risiko: <strong>{view.risk.toUpperCase()}</strong></div>
        </div>
      )}
      <div className="rounded-lg border border-brand p-3 bg-brand-bg2/40 flex gap-2 items-end">
        <select className="input-flat" value={newRule.rule_type} onChange={(e) => setNewRule({ ...newRule, rule_type: e.target.value })} data-testid="capacity-rule-type"><option value="route_max">Pr. rute</option><option value="driver_day_max">Pr. chauffør/dag</option><option value="customer_day_max">Pr. kunde/dag</option></select>
        <input type="number" className="input-flat" value={newRule.max_count} onChange={(e) => setNewRule({ ...newRule, max_count: parseInt(e.target.value || 1) })} data-testid="capacity-rule-max"/>
        <button onClick={save} className="px-3 py-1.5 bg-brand-red text-white rounded text-sm font-semibold" data-testid="capacity-rule-save">Tilføj regel</button>
      </div>
      <ul className="rounded-lg border border-brand bg-white divide-y divide-brand">
        {rules.map((r) => <li key={r.id} className="px-3 py-2 text-sm">{r.rule_type}: max {r.max_count}</li>)}
        {rules.length === 0 && <li className="px-3 py-3 text-xs text-brand-text2 italic">Ingen regler</li>}
      </ul>
    </div>
  );
}

